import {
  call,
  fork,
  put,
  select,
  takeLatest,
  takeLeading,
  takeEvery,
} from 'redux-saga/effects'
import * as T from '../api/constants'
import { 
  replace,
} from 'utils/helpers'
import {
  TRANSACTION_STATUS,
  TRAVEL_STATUS,
  IS_PRODUCTION,
  IS_DEV,
  HRIS_SYNC_STATUS,
} from 'utils/constants'
import {
  success,
  clearFromState,
  resetState,
} from 'slices/globalSlice'
import { addToChatHistory } from 'slices/customerSupportSlice'
import { setTravelQuestionnaire } from 'slices/travelSlice'
import { clearNewKYCOptions } from 'slices/kyxSlice'
import { setGeneralOptions, openErrorModal } from 'slices/appSlice'
import { EXCLUDED_DESTINATIONS } from 'components/ModalViews/AddTripProtection'
import { matchSorter } from 'match-sorter'
import { findIndex, orderBy, sortBy, isEmpty } from 'lodash'
import { boiler, getAppState, getGlobalState, getTravelState } from './sagaHelpers'

function* DEV_resetUser() {
  if (!IS_PRODUCTION) {
    const results = yield call(boiler, 'DEV_resetUser', { type: T.DEV_RESET_USER })
    if (results.status === 'success') {
      console.warn('reset user')
      yield call(getUser)
      yield put(clearNewKYCOptions())
      yield put(success({ typeLoading: T.DEV_RESET_USER }))
    }
  }
}

function* DEV_resetCorporateCustomer() {
	if (!IS_PRODUCTION) {
		const results = yield call(boiler, 'DEV_resetCorporateCustomer', {
			type: T.DEV_RESET_CORPORATE_CUSTOMER,
		})
		if (results.status === 'success') {
			console.warn('reset corporate customer')
			yield put(
				success({
					typeLoading: T.DEV_RESET_CORPORATE_CUSTOMER,
					kybOptions: {},
					kybView: '',
					beneficialOwnerOptions: {},
				}),
			)
			yield fork(getCompany)
			yield fork(getCorporateExternalAccounts)
		}
	}
}

function* DEV_verificationTest({ account_id, status }) {
  if (!IS_PRODUCTION) {
    const results = yield call(
      boiler,
      'DEV_verificationTest',
      { type: T.DEV_VERIFICATION_TEST },
      account_id,
      {
        status,
      }
    )
    if (results.status === 'success') {
      yield put(success({ typeLoading: T.DEV_VERIFICATION_TEST }))
      yield fork(getUser)
      yield fork(getCompany)
      yield fork(getExternalAccounts)
      yield fork(getCorporateExternalAccounts)
    }
  }
}

function* DEV_resetPlaidLogin({ account_id }) {
  if (!IS_PRODUCTION) {
    const results = yield call(
      boiler,
      'DEV_resetPlaidLogin',
      { type: T.DEV_RESET_PLAID_LOGIN },
      account_id,
      {
        status: '',
      }
    )
    if (results.status === 'success') {
      yield put(success({ typeLoading: T.DEV_RESET_PLAID_LOGIN }))
      yield fork(getUser)
      yield fork(getCompany)
      yield fork(getExternalAccounts)
      yield fork(getCorporateExternalAccounts)
    }
  }
}

function* DEV_resetHRISInformation() {
  if (!IS_PRODUCTION) {
    const results = yield call(boiler, 'DEV_resetHRISInformation', {
      type: T.DEV_RESET_HRIS_INFORMATION,
    })
    if (results.status === 'success') {
      console.warn('reset HRIS information')
      yield put(
        success({ typeLoading: T.DEV_RESET_HRIS_INFORMATION })
      )
      // yield call(getCompany)
    }
  }
}

function* getUser() {
  const results = yield call(boiler, 'getUser', { type: T.GET_USER })
  if (results.status === 'success') {
    const { travelQuestionnaire } = yield select(getTravelState)
    const data = {
      ...travelQuestionnaire
    }
    if (results.data?.home_airport?.length && results.data?.home_airport !== travelQuestionnaire.departing_from) {
      data.departing_from = results.data?.home_airport
    }
    yield put(setTravelQuestionnaire(data))
    yield put(success({ 
      user: results.data,
      typeLoading: T.GET_USER,
    }))
  }
}

function* setUser({ type, data }) {
  const results = yield call(boiler, 'setUser', { type }, data)
  if (results.status === 'success') {
    yield call(getUser)
    yield put(success({ typeLoading: type }))
  }
}

function* setLastLogin({ type }) {
  const results = yield call(boiler, 'setLastLogin', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, sinceLastLogin: results.data }))
}

function* getMyTrips({ year, include_itineraries }) {
  const results = yield call(
    boiler,
    'getMyTrips',
    { type: T.GET_MY_TRIPS },
    { status: [TRAVEL_STATUS.NEW, TRAVEL_STATUS.IN_PROGRESS].join(','), year, include_itineraries },
  )
  if (results.status === 'success')
    yield put(
      success({ ...results.data, typeLoading: T.GET_MY_TRIPS })
    )
}

function* getPastTrips({ year }) {
  const results = yield call(
    boiler,
    'getMyTrips',
    { type: T.GET_PAST_TRIPS },
    { status: TRAVEL_STATUS.COMPLETED.toString(), year },
  )
  if (results.status === 'success')
    yield put(
      success({
        pastTrips: results.data?.trip_data || [],
        typeLoading: T.GET_PAST_TRIPS,
      })
    )
}

function* getAllTrips({ year, include_itineraries }) {
  const results = yield call(
    boiler,
    'getMyTrips',
    { type: T.GET_ALL_TRIPS },
    { 
      status: [TRAVEL_STATUS.NEW, TRAVEL_STATUS.IN_PROGRESS, TRAVEL_STATUS.COMPLETED].join(','), 
      year, 
      sort: 'reverse', 
      include_itineraries,
    },
  )
  if (results.status === 'success') {
    yield put(
      success({
        allTrips: results.data?.trip_data || [],
        typeLoading: T.GET_ALL_TRIPS,
      })
    )
  }
}

function* getTrip({ id }) {
  const results = yield call(boiler, 'getTrip', { type: T.GET_TRIP }, id)
  if (results.status === 'success') {
    yield put(success({ trip: {}, typeLoading: T.GET_TRIP }))
    yield put(
      success({ trip: results.data, typeLoading: T.GET_TRIP })
    )
  }
}

function* getSavingsTotals() {
  const results = yield call(boiler, 'getSavingsTotals', {
    type: T.GET_SAVINGS_TOTALS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        savingsTotals: {
          ...results.data,
          // donde_credits_total: +results.data?.donde_credits_total / 100,
        },
        typeLoading: T.GET_SAVINGS_TOTALS,
      })
    )
  }
}

function* getSavingsGoal({ type }) {
  const results = yield call(boiler, 'getSavingsGoal', { type })
  if (results.status === 'success')
    yield put(success({ savingsGoal: results.data, typeLoading: type }))
}

function* setSavingsGoal({ type, data }) {
  const results = yield call(boiler, 'setSavingsGoal', { type }, data)
  if (results.status === 'success') {
    yield call(getSavingsGraph)
    yield put(success({ savingsGoal: results.data, typeLoading: type }))
  }
}

function* createPlaidLinkToken({ type, isCorporate }) {
  const payload = {}
  if (IS_DEV) payload.local = true
  const results = yield call(boiler, 'createPlaidLinkToken', { type }, payload, isCorporate)
  if (results.status === 'success') {
    const obj = isCorporate
    ? { plaidCorporateLinkToken: results.data, typeLoading: type }
    : { plaidLinkToken: results.data, typeLoading: type }
    yield put(success(obj))
  }
}

function* clearPlaidStatus({ isCorporate, accountID }) {
  const results = yield call(
    boiler,
    'clearPlaidStatus',
    { type: T.CLEAR_PLAID_STATUS },
    isCorporate,
    accountID
  )
  if (results.status === 'status')
    yield put(success({ typeLoading: T.CLEAR_PLAID_STATUS }))
}

function* relinkPlaid({ type, isCorporate, accountID, fixAccount }) {
  const results = yield call(
    boiler,
    'relinkPlaid',
    { type },
    isCorporate,
    accountID,
    fixAccount
  )
  if (results.status === 'success') {
    let obj = isCorporate
      ? { plaidCorporateRelinkLinkToken: results.data, typeLoading: type }
      : { plaidRelinkLinkToken: results.data, typeLoading: type }
    // yield call(clearPlaidStatus, { isCorporate, accountID })
    yield call(getExternalAccounts)
    if (isCorporate) yield call(getCorporateExternalAccounts)
    yield put(success(obj))
  }
}

function* getPlaidAccessToken({ type, data, auth_id, isCorporate }) {
  let payload = { data, auth_id }
  if (IS_DEV) payload.local = true
  const results = yield call(
    boiler,
    'getPlaidAccessToken',
    { type },
    payload,
    isCorporate
  )
  let obj = isCorporate
    ? { corporateExternalAccounts: results.data, typeLoading: type }
    : { externalAccounts: results.data, typeLoading: type }
  if (results.status === 'success') {
    yield put(success(obj))
    if (isCorporate) yield fork(getCompany)
    yield fork(getUser)
  }
}

function* boostAccount({ type, data }) {
  const results = yield call(boiler, 'boostAccount', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    yield fork(getSavingsHistory, { search: '' })
    yield fork(getSavingsGraph)
    yield fork(getSavingsTotals)
  }
}

function* makeTransaction({ type, data }) {
  const results = yield call(boiler, 'makeTransaction', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    yield fork(getSavingsHistory, { search: '' })
    yield fork(getSavingsGraph)
    yield fork(getSavingsTotals)
  }
}

function* makeCorporateTransaction({ type, data }) {
  const results = yield call(boiler, 'makeCorporateTransaction', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    yield fork(getCorporateTransactionHistory, { search: '' })
    yield fork(getSavingsGraph)
    yield fork(getSavingsTotals)
    yield fork(getCompany)
  }
}

function* getSavingsHistory({ search }) {
  const results = yield call(
    boiler,
    'getSavingsHistory',
    { type: T.GET_SAVINGS_HISTORY },
    search
  )
  if (results.status === 'success') {
    yield put(
      success({
        savingsHistory: results.data,
        typeLoading: T.GET_SAVINGS_HISTORY,
      })
    )
  }
}

function* getSavingsGraph() {
  const results = yield call(boiler, 'getSavingsGraph', {
    type: T.GET_SAVINGS_GRAPH,
  })
  if (results.status === 'success') {
    yield put(
      success({
        savingsGraph: results.data,
        typeLoading: T.GET_SAVINGS_GRAPH,
      })
    )
  }
}

function* getAccountDetails({ type }) {
  const results = yield call(boiler, 'getAccountDetails', { type })
  if (results.status === 'success')
    yield put(
      success({ accountDetails: results.data, typeLoading: type })
    )
}

function* setAccountDetails({ type, data }) {
  const results = yield call(boiler, 'setAccountDetails', { type }, data)
  if (results.status === 'success') {
    const { accountDetails } = yield select(getGlobalState)
    yield put(
      success({
        accountDetails: { ...accountDetails, ...results.data },
        typeLoading: type,
      })
    )
  }
}

function* getTravelCompanions({ type }) {
  const results = yield call(boiler, 'getTravelCompanions', { type })
  if (results.status === 'success')
    yield put(success({ ...results.data, typeLoading: type }))
}

function* getTravelPassports({ type }) {
  const results = yield call(boiler, 'getTravelPassports', { type })
  if (results.status === 'success')
    yield put(success({ passports: results.data, typeLoading: type }))
}

function* createTravelPassport({ type, data = {} }) {
  const results = yield call(boiler, 'createTravelPassport', { type }, data)
  if (results.status === 'success') {
    const { passports = [] } = yield select(getGlobalState)
    const data = passports.length
      ? [...passports, results.data]
      : [results.data]
    yield put(success({ passports: data, typeLoading: type }))
    yield fork(getUser)
  }
}

function* setTravelPassport({ type, data, id }) {
  const results = yield call(boiler, 'setTravelPassport', { type }, data, id)
  if (results.status === 'success') {
    const { passports } = yield select(getGlobalState)
    yield put(
      success({
        passports: replace(passports, { id }, results.data),
        typeLoading: type,
      })
    )
    yield fork(getUser)
  }
}

function* deleteTravelPassport({ type, id }) {
  const results = yield call(boiler, 'deleteTravelPassport', { type }, id)
  if (results.status === 'success') {
    const { passports } = yield select(getGlobalState)
    yield put(
      success({
        passports: replace(passports, { id }),
        typeLoading: type,
      })
    )
    yield fork(getUser)
  }
}

function* getTravelLoyalties({ type }) {
  const results = yield call(boiler, 'getTravelLoyalties', { type })
  if (results.status === 'success')
    yield put(
      success({ travelLoyalties: results.data, typeLoading: type })
    )
}

function* setTravelLoyalty({ type, program_id, number }) {
  const results = yield call(
    boiler,
    'setTravelLoyalty',
    { type },
    { program_id, number }
  )
  if (results.status === 'success')
    yield put(
      success({ travelLoyalties: results.data, typeLoading: type })
    )
}

function* updateTravelLoyalty({ type, program_id, number, loyaltyKey, id }) {
  const results = yield call(
    boiler,
    'updateTravelLoyalty',
    { type },
    { program_id, number },
    id
  )
  if (results.status === 'success') {
    const { travelLoyalties } = yield select(getGlobalState)
    const temp = yield replace(
      travelLoyalties[loyaltyKey],
      { id },
      {
        id,
        number: results.data?.number,
        iata_code: results.data?.program?.iata_code,
        name: results.data?.program?.name,
      } || {}
    )
    yield put(
      success({
        travelLoyalties: { ...travelLoyalties, [loyaltyKey]: temp },
        typeLoading: type,
      })
    )
  }
}

function* deleteTravelLoyalty({ type, loyaltyKey, id }) {
  if (id) {
    const results = yield call(boiler, 'deleteTravelLoyalty', { type }, id)
    const { travelLoyalties } = yield select(getGlobalState)
    const temp = yield id
      ? replace(travelLoyalties[loyaltyKey], { id })
      : replace(travelLoyalties[loyaltyKey], { temp: true })
    if (results.status === 'success')
      yield put(
        success({
          travelLoyalties: { ...travelLoyalties, [loyaltyKey]: temp },
          typeLoading: type,
        })
      )
  } else {
    const { travelLoyalties } = yield select(getGlobalState)
    const temp = yield replace(travelLoyalties[loyaltyKey], { temp: true })
    yield put(
      success({
        travelLoyalties: { ...travelLoyalties, [loyaltyKey]: temp },
        typeLoading: type,
      })
    )
  }
}

function* getTravelLoyaltyByType({ type, program_id }) {
  const results = yield call(
    boiler,
    'getTravelLoyaltyByType',
    { type },
    program_id
  )
  if (results.status === 'success') {
    let sortedArr = results.data || []
    sortedArr.sort((a, b) => (a.name > b.name ? 1 : -1))
    yield put(
      success({
        [`loyaltyPrograms_${program_id}`]: sortedArr,
        typeLoading: type,
      })
    )
  }
}

function* getSetupSteps({ type }) {
  const results = yield call(boiler, 'getSetupSteps', { type })
  if (results.status === 'success')
    yield put(success({ setupSteps: results.data, typeLoading: type }))
}

function* getSavingsAccounts({ type }) {
  const results = yield call(boiler, 'getSavingsAccounts', { type })
  if (results.status === 'success')
    yield put(
      success({ savingsAccounts: results.data, typeLoading: type })
    )
}

function* getSavingsPaymentMethods({ type }) {
  const results = yield call(boiler, 'getSavingsPaymentMethods', { type })
  if (results.status === 'success')
    yield put(
      success({
        savingsPaymentMethods: results.data,
        typeLoading: type,
      })
    )
}

function* getNotificationPreferences({ type }) {
  const results = yield call(boiler, 'getNotificationPreferences', { type })
  if (results.status === 'success')
    yield put(
      success({
        notificationPreferences: results.data,
        typeLoading: type,
      })
    )
}

function* getIntentions({ type }) {
  const results = yield call(boiler, 'getIntentions', { type })
  if (results.status === 'success')
    yield put(success({ intentions: results.data, typeLoading: type }))
}

function* getOnboarding({ type }) {
  const results = yield call(boiler, 'getOnboarding', { type })
  if (results.status === 'success')
    yield put(success({ onboarding: results.data, typeLoading: type }))
}

function* getOnboardingDocuments({ type }) {
  const results = yield call(boiler, 'getOnboardingDocuments', { type })
  if (results.status === 'success')
    yield put(success({ onboardingDocuments: results.data, typeLoading: type }))
}

function* getOnboardingDocumentStatus({ type, doc_id }) {
  const results = yield call(boiler, 'getOnboardingDocumentStatus', {
    type,
    doc_id,
  })
  if (results.status === 'success') {
    const { onboardingDocStatus = {} } = yield select(getGlobalState)
    yield put(
      success({
        onboardingDocStatus: { ...onboardingDocStatus, ...results.data },
        typeLoading: type,
      })
    )
  }
}

function* getOnboardingAgreements({ type }) {
  const results = yield call(boiler, 'getOnboardingAgreements', { type })
  if (results.status === 'success')
    yield put(
      success({
        agreements: results.data?.agreements,
        typeLoading: type,
      })
    )
}

function* setOnboardingAgreements({ type, agreements, agreement_type }) {
  const results = yield call(
    boiler,
    'setOnboardingAgreements',
    { type },
    { agreements }
  )
  if (results.status === 'success')
    yield call(getUser)
    yield put(success({ typeLoading: type, [`acceptance_id_${agreement_type}`]: results.data?.acceptance_id }))
}

function* setOnboardingGeneralInfo({ type, data }) {
  const results = yield call(boiler, 'setOnboardingGeneralInfo', { type }, data)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    yield put(
      success({
        user: { ...user, ...results.data },
        typeLoading: type,
      })
    )
  }
}

function* setOnboardingIncomeInfo({ type, data }) {
  const results = yield call(boiler, 'setOnboardingIncomeInfo', { type }, data)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    yield put(
      success({
        user: { ...user, ...results.data },
        typeLoading: type,
      })
    )
  }
}

function* setOnboardingPersonalIdentification({ type, data }) {
  const results = yield call(
    boiler,
    'setOnboardingPersonalIdentification',
    { type },
    data
  )
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    let returnData = { user: { ...user, ...results.data }, typeLoading: type }
    // if (!results.data?.has_savings_account) {
    //   yield put(openErrorModal({ error: { code: 'onboarding_go_back' } }))
    // }
    yield put(success(returnData))
  }
}

function* getOnboardingStatus({ type }) {
  const results = yield call(boiler, 'getOnboardingStatus', { type })
  if (results.status === 'success') {
    if (typeof results.data === 'object') {
      const { user } = yield select(getGlobalState)
      yield put(
        success({
          user: {
            ...user,
            savings_account_status: results.data.status,
            savings_account_sub_status: results.data.sub_status,
            has_savings_account: results.data.status === 2,
          },
          typeLoading: type,
        })
      )
    } else {
      yield put(success({ typeLoading: type }))
    }
  }
}

function* getKBAQuestions({ data }) {
  const results = yield call(
    boiler,
    'getKBAQuestions',
    { type: T.GET_KBA_QUESTIONS },
    data
  )
  if (results.status === 'success')
    yield put(
      success({
        kbaQuestions: results.data,
        typeLoading: T.GET_KBA_QUESTIONS,
      })
    )
}

function* submitKBAAnswers({ answers }) {
  const { kbaSessionId } = yield select(getGlobalState)
  const results = yield call(
    boiler,
    'submitKBAAnswers',
    { type: T.SUBMIT_KBA_ANSWERS },
    { session_id: kbaSessionId, answers }
  )
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.SUBMIT_KBA_ANSWERS,
        user: results.data,
      })
    )
  }
}

function* initKBA({ type }) {
  const results = yield call(boiler, 'initKBA', { type })
  if (results.status === 'success') {
    yield put(
      success({ typeLoading: type, kbaSessionId: results.data })
    )
    yield call(getKBAQuestions, { data: { session_id: results.data } })
  }
}

function* getCorporateOnboarding({ type }) {
  const results = yield call(boiler, 'getCorporateOnboarding', { type })
  if (results.status === 'success')
    yield put(
      success({ corporateOnboarding: results.data, typeLoading: type })
    )
}

function* setCorporateOnboarding({ type, data }) {
  const results = yield call(boiler, 'setCorporateOnboarding', { type }, data)
  if (results.status === 'success') {
    yield call(getUser)
    if (data.has_beneficial_owners) yield call(getCompany)
    yield put(success({ typeLoading: type, code: results.code }))
  }
}

function* getCorporateOnboardingDocuments() {
  const results = yield call(boiler, 'getCorporateOnboardingDocuments', {
    type: T.GET_CORPORATE_ONBOARDING_DOCUMENTS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        corporateOnboardingDocuments: results.data,
        typeLoading: T.GET_CORPORATE_ONBOARDING_DOCUMENTS,
      })
    )
  }
}

function* setCorporateOnboardingDocuments({ type, data }) {
  const results = yield call(
    boiler,
    'setCorporateOnboardingDocuments',
    { type },
    data
  )
  if (results.status === 'success') {
    yield call(getUser)
    // yield call(getCompany)
    yield call(getCorporateOnboardingDocuments)
    yield put(
      success({
        company: results.data?.customer,
        corpDocID: results.data?.document,
        typeLoading: type,
        code: results.code,
      })
    )
  }
}

function* getCorporateOnboardingAgreements({ type }) {
  const results = yield call(boiler, 'getCorporateOnboardingAgreements', {
    type,
  })
  if (results.status === 'success')
    yield put(
      success({
        corporateAgreements: results.data?.agreements,
        typeLoading: type,
      })
    )
}

function* setCorporateOnboardingAgreements({ type, corporateAgreements }) {
  const results = yield call(
    boiler,
    'setOnboardingAgreements',
    { type },
    { agreements: corporateAgreements }
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* createBeneficialOwner({ type, data }) {
  const results = yield call(boiler, 'createBeneficialOwner', { type }, data)
  if (results.status === 'success') {
    yield call(getCompany)
    yield put(success({ typeLoading: type }))
  }
}

function* getBeneficialOwnerDetails({ type, data }) {
  const results = yield call(
    boiler,
    'getBeneficialOwnerDetails',
    { type },
    data
  )
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: type,
        currentBeneficialOwner: results.data,
      })
    )
  }
}

function* getBeneficialOwners() {
  const results = yield call(boiler, 'getBeneficialOwners', {
    type: T.GET_BENEFICIAL_OWNERS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_BENEFICIAL_OWNERS,
        beneficialOwners: results.data,
      })
    )
}

function* deleteBeneficialOwner({ type, bo_id }) {
  const results = yield call(
    boiler,
    'deleteBeneficialOwner',
    { type },
    { bo_id }
  )
  if (results.status === 'success') {
    yield call(getBeneficialOwners)
    yield put(success({ typeLoading: type }))
  }
}

function* certifyBeneficialOwners({ type }) {
  const results = yield call(boiler, 'certifyBeneficialOwners', { type })
  if (results.status === 'success') {
    yield call(getBeneficialOwners)
    yield call(getCompany)
    yield put(success({ typeLoading: type }))
  }
}

function* getContributionSettings() {
  const results = yield call(boiler, 'getContributionSettings', {
    type: T.GET_CONTRIBUTION_SETTINGS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        contributionSettings: results.data,
        typeLoading: T.GET_CONTRIBUTION_SETTINGS,
      })
    )
  }
}

function* createContributionSettings({ type, data }) {
  const results = yield call(
    boiler,
    'createContributionSettings',
    { type },
    data
  )
  // const { onboarding } = yield select(getGlobalState)
  if (results.status === 'success')
    yield put(
      success({
        contributionSettings: results.data,
        typeLoading: type,
      })
    )
  // if (results.status === 'success') yield put(success({ onboarding: { ...onboarding, ...data }, contributionSettings: results.data, typeLoading: type }))
}

function* updateContributionSettings({ type, data }) {
  const results = yield call(
    boiler,
    'updateContributionSettings',
    { type },
    data
  )
  if (results.status === 'success') {
    yield fork(getContributionSettings)
    yield fork(getSavingsGraph)
    yield put(success({ typeLoading: type }))
  }
}

function* getExternalAccounts() {
  const results = yield call(boiler, 'getExternalAccounts', {
    type: T.GET_EXTERNAL_ACCOUNTS,
  })
  if (results.status === 'success')
    yield put(
      success({
        externalAccounts: results.data,
        typeLoading: T.GET_EXTERNAL_ACCOUNTS,
      })
    )
}

function* deleteExternalAccount({ type, id }) {
  const results = yield call(boiler, 'deleteExternalAccount', { type }, id)
  if (results.status === 'success') {
    // const { externalAccounts } = yield select(getGlobalState)
    yield call(getExternalAccounts)
    yield put(
      success({
        // externalAccounts: replace(externalAccounts, { id }),
        typeLoading: type,
      })
    )
    yield fork(getUser)
  }
}

function* getCorporateExternalAccounts() {
  const results = yield call(boiler, 'getCorporateExternalAccounts', {
    type: T.GET_CORPORATE_EXTERNAL_ACCOUNTS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        corporateExternalAccounts: results.data,
        typeLoading: T.GET_CORPORATE_EXTERNAL_ACCOUNTS,
      })
    )
  }
}

function* deleteCorporateExternalAccount({ type, id }) {
  const results = yield call(
    boiler,
    'deleteCorporateExternalAccount',
    { type },
    id
  )
  if (results.status === 'success') {
    // const { corporateExternalAccounts } = yield select(getGlobalState)
    yield call(getCorporateExternalAccounts)
    yield put(
      success({
        // corporateExternalAccounts: replace(corporateExternalAccounts, { id }),
        typeLoading: type,
      })
    )
    yield fork(getCompany)
  }
}

function* getPTOExplainer({ type }) {
  const results = yield call(boiler, 'getPTOExplainer', { type })
  if (results.status === 'success')
    yield put(
      success({ PTOExplainer: results.data, typeLoading: type })
    )
}

function* getEmployees() {
  const { generalOptions } = yield select(getAppState)
  const { employeeTableParams } = window.location.pathname === '/admin/user-management' ? generalOptions : {}
  const results = yield call(boiler, 'getEmployees', { type: T.GET_EMPLOYEES }, employeeTableParams)
  if (results.status === 'success') {
    let temp = {
      employees: results.data?.results || results.data,
      employeeCount: results.data?.count || 0,
      typeLoading: T.GET_EMPLOYEES,
    }
    if (!employeeTableParams) {
      temp.hasUsers = findIndex(results.data?.results || results.data, { role: 'User' }) > -1
    }
    yield put(success(temp))
  }
}

function* getManagers() {
  const results = yield call(boiler, 'getManagers', {
    type: T.GET_MANAGERS,
  })
  if (results.status === 'success')
    yield put(
      success({
        managers: results.data,
        typeLoading: T.GET_MANAGERS,
      })
    )
}

function* getTeam() {
  const results = yield call(boiler, 'getTeam', { type: T.GET_TEAM })
  if (results.status === 'success')
    yield put(
      success({
        team: results.data,
        typeLoading: T.GET_TEAM,
      })
    )
}

function* getManagerTeam({ manager_id }) {
  const results = yield call(
    boiler,
    'getManagerTeam',
    { type: T.GET_MANAGER_TEAM },
    manager_id
  )
  if (results.status === 'success')
    yield put(
      success({
        managerTeam: results.data,
        typeLoading: T.GET_MANAGER_TEAM,
      })
    )
}

function* createEmployee({ type, data }) {
  const results = yield call(boiler, 'createEmployee', { type }, data)
  if (results.status === 'success') {
    yield call(getEmployees)
    yield call(getCompany)
    yield put(success({ typeLoading: type }))
  }
}

function* updateEmployee({ type, unique_id, data, role }) {
  if (role) yield call(setEmployeeRole, { unique_id, role })
  const results = yield call(boiler, 'updateEmployee', { type }, data, unique_id)
  if (results.status === 'success') {
    const { employees } = yield select(getGlobalState)
    const { generalOptions } = yield select(getAppState)
    yield put(setGeneralOptions({
      ...generalOptions,
      selectedUser: results.data?.user,
    }))
    yield put(
      success({
        employees: replace(employees, { unique_id }, results.data?.user),
        typeLoading: type,
      })
    )
  }
}

function* uploadEmployees({ type, data, delay }) {
  const results = yield call(
    boiler,
    'uploadEmployees',
    { type, overrideError: true },
    data,
    delay
  )
  if (results.status === 'success') {
    yield call(getEmployees)
    yield call(getCompany)
    yield put(success({ typeLoading: type }))
  }
}

function* deleteEmployee({ type, unique_id }) {
  const results = yield call(boiler, 'deleteEmployee', { type }, unique_id)
  if (results.status === 'success') {
    yield call(getCompany)
    const { employees } = yield select(getGlobalState)
    yield put(
      success({
        employees: replace(employees, { unique_id }),
        typeLoading: type,
      })
    )
  }
}

function* setEmployeeRole({ unique_id, role }) {
  const results = yield call(boiler, 'setEmployeeRole', { type: T.SET_EMPLOYEE_ROLE }, unique_id, { role })
  if (results.status === 'success') {
    const { employees } = yield select(getGlobalState)
    const { generalOptions } = yield select(getAppState)
    yield put(setGeneralOptions({
      ...generalOptions,
      selectedUser: {
        ...(generalOptions.selectedUser || {}),
        role,
      },
    }))
    yield put(
      success({
        employees: replace(employees, { unique_id }, { role }),
        typeLoading: T.SET_EMPLOYEE_ROLE,
      })
    )
  }
}

function* getBusinessClassifications({ type }) {
  const results = yield call(boiler, 'getBusinessClassifications', { type })
  if (results.status === 'success')
    yield put(
      success({
        businessClassifications: results.data['business-classifications'],
        typeLoading: type,
      })
    )
}

function* getSurvey({ type }) {
  const results = yield call(boiler, 'getSurvey', { type })
  if (results.status === 'success')
    yield put(success({ survey: results.data, typeLoading: type }))
}

function* setSurvey({ type, data }) {
  const results = yield call(boiler, 'setSurvey', { type }, data)
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
  // const { survey } = yield select(getGlobalState)
  // if (results.status === 'success') yield put(success({ survey: replace(survey, { id: data.survey_id }), typeLoading: type }))
}

function* setTravelIntention({ type, travel_intention }) {
  const results = yield call(boiler, 'setUser', { type }, { travel_intention })
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    yield call(getUser)
  }
}

function* getCompany() {
  const results = yield call(boiler, 'getCompany', { type: T.GET_COMPANY })
  if (results.status === 'success')
    yield put(
      success({
        company: results.data,
        typeLoading: T.GET_COMPANY,
      })
    )
}

function* setCompany({ type, data }) {
  const results = yield call(boiler, 'setCompany', { type }, data)
  if (results.status === 'success') {
    yield fork(getUser)
    yield put(success({ company: results.data, typeLoading: type }))
  }
}

function* getCountries({ type, search }) {
  const results = yield call(boiler, 'getCountries', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        countriesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ countriesSearchResults: [], typeLoading: type })
    )
  }
}

function* getAirports({ type, search }) {
  const results = yield call(boiler, 'getAirports', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        airportsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ airportsSearchResults: [], typeLoading: type }))
  }
}

function* getAirlines({ type, search }) {
  const results = yield call(boiler, 'getAirlines', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        airlinesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ airlinesSearchResults: [], typeLoading: type }))
  }
}

function* getStays({ type, search, stoken }) {
  yield put(clearFromState(['staysSearchResults']))
	const results = yield call(boiler, 'getStays', { type }, search, stoken)
	if (results.status === 'success') {
		// const tempCities = matchSorter(results.data.cities || [], '', { keys: ['structured_formatting.secondary_text', 'structured_formatting.main_text', 'description'] })
		const tempHotels = matchSorter(results.data.hotels || [], '', { keys: ['city', 'state', 'state_short', 'name'] })
		yield put(
			success({ staysSearchResults: {
				cities: results.data.cities,
				hotels: tempHotels,
			}, typeLoading: type }),
		)
	} else {
		yield put(success({ staysSearchResults: {}, typeLoading: type }))
	}
}

function* getLocations({ type, search, stoken }) {
  // TODO
  yield put(clearFromState(['locationsSearchResults']))
	const results = yield call(boiler, 'getLocations', { type }, search, stoken)
	if (results.status === 'success') {
    yield put(success({ 
      locationsSearchResults: results.data.cities || [],
        // results.data.cities?.length <= 1 ?
        // results.data.cities : 
        // matchSorter(results.data.cities || [], search, { keys: ['structured_formatting.secondary_text', 'structured_formatting.main_text', 'description'] }),
      typeLoading: type,
    }))
	} else {
		yield put(success({ locationsSearchResults: [], typeLoading: type }))
	}
}

function* getOceanCruises({ type, search }) {
  const results = yield call(boiler, 'getOceanCruises', { type }, search)
  if (results.status === 'success') {
    const data = search.length
      ? results.data
      : [{ vendor_id: '-1', code: null, name: 'All' }, ...results.data]
    yield put(
      success({ oceanCruisesSearchResults: data, typeLoading: type })
    )
  } else {
    yield put(
      success({ oceanCruisesSearchResults: [], typeLoading: type })
    )
  }
}

function* getOceanDestinations({ type, search }) {
  const results = yield call(boiler, 'getOceanDestinations', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        oceanDestinationsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({
        oceanDestinationsSearchResults: [],
        typeLoading: type,
      })
    )
  }
}

function* getOceanCruiseTours({ type, search }) {
  const results = yield call(boiler, 'getOceanCruiseTours', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        oceanCruiseToursSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ oceanCruiseToursSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverCruises({ type, search }) {
  const results = yield call(boiler, 'getRiverCruises', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverCruisesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverCruisesSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverDestinations({ type, search }) {
  const results = yield call(boiler, 'getRiverDestinations', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverDestinationsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({
        riverDestinationsSearchResults: [],
        typeLoading: type,
      })
    )
  }
}

function* getRiverCities({ type, search }) {
  const results = yield call(boiler, 'getRiverCities', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverCitiesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverCitiesSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverCountries({ type, search }) {
  const results = yield call(boiler, 'getRiverCountries', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverCountriesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverCountriesSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverLines({ type, search }) {
  const results = yield call(boiler, 'getRiverLines', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverLinesSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverLinesSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverShips({ type, search }) {
  const results = yield call(boiler, 'getRiverShips', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverShipsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverShipsSearchResults: [], typeLoading: type })
    )
  }
}

function* getRiverWaterways({ type, search }) {
  const results = yield call(boiler, 'getRiverWaterways', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        riverWaterwaysSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ riverWaterwaysSearchResults: [], typeLoading: type })
    )
  }
}

function* getTourVendors({ type, search }) {
  const results = yield call(boiler, 'getTourVendors', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        tourVendorsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ tourVendorsSearchResults: [], typeLoading: type })
    )
  }
}

function* getCarVendors({ type, search }) {
  const results = yield call(boiler, 'getCarVendors', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        carVendorsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ carVendorsSearchResults: [], typeLoading: type })
    )
  }
}

function* getExperienceLocations({ type, search, search_date }) {
  const results = yield call(
    boiler,
    'getExperienceLocations',
    { type },
    search,
    search_date
  )
  if (results.status === 'success') {
    yield put(
      success({
        experienceLocationsSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({
        experienceLocationsSearchResults: [],
        typeLoading: type,
      })
    )
  }
}

function* getExperiences({ type, location_id }) {
  const results = yield call(boiler, 'getExperiences', { type }, location_id)
  if (results.status === 'success') {
    yield put(
      success({
        experiences: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ experiences: [], typeLoading: type }))
  }
}

function* searchViator({ type, search_term, params }) {
  const results = yield call(
    boiler,
    'searchViator',
    { type },
    search_term,
    params,
  )
  if (results.status === 'success') {
    yield put(
      success({
        aiViatorExperiences: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ aiViatorExperiences: [], typeLoading: type }))
  }
}

function* getViatorExperiences({ type, location_id, params }) {
  const results = yield call(
    boiler,
    'getViatorExperiences',
    { type },
    location_id,
    params
  )
  if (results.status === 'success') {
    yield put(
      success({
        viatorExperiences: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ viatorExperiences: [], typeLoading: type }))
  }
}

function* getViatorAvailability({ type, product_id, data }) {
  const results = yield call(
    boiler,
    'getViatorAvailability',
    { type },
    product_id,
    data
  )
  if (results.status === 'success') {
    yield put(
      success({
        viatorAvailability: results.data,
        typeLoading: type,
      })
    )
  }
}

function* getExperienceTags({ type }) {
  const results = yield call(boiler, 'getExperienceTags', { type })
  if (results.status === 'success') {
    yield put(
      success({
        experienceTags: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ experienceTags: [], typeLoading: type }))
  }
}

function* uploadIDDocument({ type, data }) {
  const results = yield call(boiler, 'uploadIDDocument', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, user: results.data }))
  }
}

function* getCorporateOnboardingStatus({ type }) {
  const results = yield call(boiler, 'getCorporateOnboardingStatus', { type })
  if (results.status === 'success') {
    const { company } = yield select(getGlobalState)
    if (typeof results.data === 'object') {
      yield put(
        success({
          company: { ...company, ...results.data },
          typeLoading: type,
        })
      )
    } else {
      yield put(success({ typeLoading: type }))
    }
  }
}

function* getCorporateDocumentStatus({ type, doc_id }) {
  const results = yield call(boiler, 'getCorporateDocumentStatus', {
    type,
    doc_id,
  })
  if (results.status === 'success') {
    const { corporateOnboardingDocStatus = {} } = yield select(getGlobalState)
    yield put(
      success({
        corporateOnboardingDocStatus: {
          ...corporateOnboardingDocStatus,
          ...results.data,
        },
        typeLoading: type,
      })
    )
  }
}

function* getAuthorizationText({ type }) {
  const results = yield call(boiler, 'getAuthorizationText', { type })
  if (results.status === 'success')
    yield put(
      success({ authorizationText: results.data, typeLoading: type })
    )
}

function* completeAccount({ type, id, isCorporate }) {
  const results = yield call(
    boiler,
    'completeAccount',
    { type },
    isCorporate,
    id
  )
  if (results.status === 'success') {
    // yield call(isCorporate ? getCorporateExternalAccounts : getExternalAccounts)
    yield put(success({ typeLoading: type }))
    yield fork(getUser)
    if (isCorporate) yield fork(getCompany)
  }
}

function* clearAccountError({ type, id, isCorporate }) {
  const results = yield call(
    boiler,
    'clearAccountError',
    { type },
    isCorporate,
    id
  )
  if (results.status === 'success') {
    if (!isCorporate) yield call(getExternalAccounts)
    if (isCorporate) yield call(getCorporateExternalAccounts)
    yield put(success({ typeLoading: type }))
  }
}

function* getCorporateTransactionHistory({ month, year, page, page_size }) {
  const results = yield call(
    boiler,
    'getCorporateTransactionHistory',
    { type: T.GET_CORPORATE_TRANSACTION_HISTORY },
    month,
    year,
    page,
    page_size,
  )
  if (results.status === 'success') {
    yield put(
      success({
        corporateTransactionHistory: results.data,
        typeLoading: T.GET_CORPORATE_TRANSACTION_HISTORY,
      })
    )
  }
}

function* getCorporateWalletHistory({ month, year, page, page_size }) {
  const results = yield call(
    boiler,
    'getCorporateWalletHistory',
    { type: T.GET_CORPORATE_WALLET_HISTORY },
    month,
    year,
    page,
    page_size,
  )
  if (results.status === 'success') {
    yield put(
      success({
        corporateWalletHistory: results.data,
        typeLoading: T.GET_CORPORATE_WALLET_HISTORY,
      })
    )
  }
}

function* cancelTransaction({ type, id }) {
  const results = yield call(boiler, 'cancelTransaction', { type }, id)
  if (results.status === 'success') {
    const { savingsHistory } = yield select(getGlobalState)
    let newHistory = []
    for (let i = 0; i < savingsHistory.length; i++) {
      let deposits = []
      for (let j = 0; j < savingsHistory[i].deposits.length; j++) {
        if (savingsHistory[i].deposits[j].id === id) {
          let newDeposit = savingsHistory[i].deposits[j]
          newDeposit.status = TRANSACTION_STATUS.CANCELLING
          deposits.push(newDeposit)
        } else {
          deposits.push(savingsHistory[i].deposits[j])
        }
      }
      newHistory.push(savingsHistory[i])
    }
    yield put(
      success({ savingsHistory: newHistory, typeLoading: type })
    )
  }
}

function* cancelCorporateTransaction({ type, id }) {
  const results = yield call(boiler, 'cancelTransaction', { type }, id)
  if (results.status === 'success') {
    const { corporateTransactionHistory } = yield select(getGlobalState),
      { transactions } = corporateTransactionHistory || {}

    let newHistory = []
    for (let i = 0; i < transactions.length; i++) {
      let deposits = []
      for (let j = 0; j < transactions[i].deposits.length; j++) {
        if (transactions[i].deposits[j].id === id) {
          let newDeposit = transactions[i].deposits[j]
          newDeposit.status = TRANSACTION_STATUS.CANCELLING
          deposits.push(newDeposit)
        } else {
          deposits.push(transactions[i].deposits[j])
        }
      }
      newHistory.push(transactions[i])
    }
    yield put(
      success({
        corporateTransactionHistory: {
          ...corporateTransactionHistory,
          transactions: newHistory,
        },
        typeLoading: type,
      })
    )
  }
}

function* cancelManagerTransaction({ type, id }) {
  const results = yield call(boiler, 'cancelTransaction', { type }, id)
  if (results.status === 'success') {
    const { budget } = yield select(getGlobalState),
      { transactions } = budget || {}

    let newHistory = []
    transactions.forEach(transaction => {
      if (transaction.id === id) {
        newHistory.push({
          ...transaction,
          status: TRANSACTION_STATUS.CANCELLING,
        })
      } else {
        newHistory.push(transaction)
      }
    })
    yield put(
      success({
        budget: {
          ...budget,
          transactions: newHistory,
        },
        typeLoading: type,
      })
    )
  }
}

function* setBeneficialOwnerDocuments({ type, data }) {
  const results = yield call(
    boiler,
    'setBeneficialOwnerDocuments',
    { type },
    data
  )
  if (results.status === 'success') {
    yield call(getBeneficialOwners)
    yield call(getCompany)
    yield put(success({ typeLoading: type, code: results.code }))
  }
}

function* getBeneficialOwnerStatus({ type }) {
  const results = yield call(boiler, 'getBeneficialOwnerStatus', { type })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: type,
        beneficialOwnersStatus: results.data,
      })
    )
}

function* encryptToken({ type }) {
  const { token } = yield select(getGlobalState)
  const results = yield call(
    boiler,
    'encryptToken',
    { type },
    `{"donde_tdata":"${token}"}`
  )
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, eToken: results.data.etoken })
    )
}

function* getInternalOnboardingAgreements({ type }) {
  const results = yield call(boiler, 'getInternalOnboardingAgreements', {
    type,
  })
  if (results.status === 'success')
    yield put(
      success({
        internalAgreements: results.data?.agreements,
        typeLoading: type,
      })
    )
}

function* getInternalCorpOnboardingAgreements({ type }) {
  const results = yield call(boiler, 'getInternalCorpOnboardingAgreements', {
    type,
  })
  if (results.status === 'success')
    yield put(
      success({
        internalCorpAgreements: results.data?.agreements,
        typeLoading: type,
      })
    )
}

function* getCompanyMatch({ type }) {
  const results = yield call(boiler, 'getCompanyMatch', { type })
  if (results.status === 'success')
    yield put(
      success({ companyMatch: results.data, typeLoading: type })
    )
}

function* createBookingRequest({ type, data }) {
  const results = yield call(boiler, 'createBookingRequest', { type }, data)
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* getExperienceDetails({ type, id }) {
  const results = yield call(boiler, 'getExperienceDetails', { type }, id)
  if (results.status === 'success')
    yield put(success({ experience: results.data, typeLoading: type }))
}

function* getContentDetails({ id }) {
  const results = yield call(boiler, 'getContentDetails', { type: T.GET_CONTENT_DETAILS }, id)
  if (results.status === 'success')
    yield put(success({ experience: results.data, typeLoading: T.GET_CONTENT_DETAILS }))
}

function* getContent({ type, vendor, tags, categories, selector }) {
  const results = yield call(
    boiler,
    'getContent',
    { type },
    vendor,
    tags,
    categories
  )
  if (results.status === 'success') {
    yield put(
      success({
        [selector || 'experiences']: results.data,
        typeLoading: type,
      })
    )
  }
}

function* getCategories({ type }) {
  const results = yield call(boiler, 'getCategories', { type })
  if (results.status === 'success')
    yield put(success({ categories: results.data, typeLoading: type }))
}

function* getVendors({ type, category_id }) {
  const results = yield call(boiler, 'getVendors', { type }, category_id)
  if (results.status === 'success')
    yield put(
      success({
        [`vendors${category_id || ''}`]: results.data,
        typeLoading: type,
      })
    )
}

function* getFeaturedVendors({ type }) {
  const results = yield call(boiler, 'getFeaturedVendors', { type })
  if (results.status === 'success')
    yield put(
      success({ featuredVendors: results.data, typeLoading: type })
    )
}

function* getVendor({ type, id }) {
  const results = yield call(boiler, 'getVendor', { type }, id)
  if (results.status === 'success')
    yield put(success({ vendor: results.data, typeLoading: type }))
}

function* getFAQCategories({ type }) {
  const results = yield call(boiler, 'getFAQCategories', { type })
  if (results.status === 'success')
    yield put(success({ faqCategories: results.data, typeLoading: type }))
}

function* getFAQ({ type, faqType }) {
  const results = yield call(boiler, 'getFAQ', { type }, faqType)
  if (results.status === 'success')
    yield put(success({ faq: results.data, typeLoading: type }))
}

function* bulkEmployeeDelete({ type, user_ids }) {
  const results = yield call(
    boiler,
    'bulkEmployeeDelete',
    { type },
    { user_ids }
  )
  if (results.status === 'success') {
    yield call(getEmployees)
    yield call(getCompany)
    yield put(
      success({ typeLoading: type, bulkDeleteResults: results.data })
    )
  }
}

function* bulkEmployeeEdit({
  type,
  user_ids,
  manager_id,
  employee_type,
  policy_id,
}) {
  let payload = { user_ids }
  if (manager_id) payload.manager_id = manager_id
  if (employee_type) payload.employee_type = employee_type
  if (policy_id) payload.policy_id = policy_id
  const results = yield call(boiler, 'bulkEmployeeEdit', { type }, payload)
  if (results.status === 'success') {
    yield call(getEmployees)
    yield put(success({ typeLoading: type }))
  }
}

function* bulkEmployeeInvite({ type, users }) {
  const { selectedUsers = [] } = yield select(getGlobalState)
  const results = yield call(
      boiler,
      'bulkEmployeeInvite',
      { type },
      { user_ids: users || selectedUsers }
    )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* inviteInactiveUsers({ type }) {
  const results = yield call(boiler, 'inviteInactiveUsers', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* searchEmployees({ type, search }) {
  const results = yield call(boiler, 'searchEmployees', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        employeeSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ employeeSearchResults: [], typeLoading: type }))
  }
}

function* searchManagers({ type, search }) {
  const results = yield call(boiler, 'searchManagers', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        managerSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(success({ managerSearchResults: [], typeLoading: type }))
  }
}

function* getEmployeeUploadResults({ type }) {
  const results = yield call(boiler, 'getEmployeeUploadResults', { type })
  if (results.status === 'success')
    yield put(
      success({
        employeeUploadResults: results.data,
        typeLoading: type,
      })
    )
}

function* inviteUser({ type, user_id }) {
  const results = yield call(boiler, 'inviteUser', { type }, user_id)
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* makeBulkCorporateTransaction({ type, data }) {
  const results = yield call(
    boiler,
    'makeBulkCorporateTransaction',
    { type },
    data
  )
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    yield fork(getCorporateTransactionHistory, { search: '' })
    yield fork(getSavingsGraph)
    yield fork(getSavingsTotals)
    yield fork(getCompany)
  }
}

function* getBulkEmployeeRewardAttempts({ type }) {
  const results = yield call(boiler, 'getBulkEmployeeRewardAttempts', { type })
  if (results.status === 'success')
    yield put(
      success({ rewardAttempts: results.data, typeLoading: type })
    )
}

function* getEmployeeUploadLog({ type }) {
  const results = yield call(boiler, 'getEmployeeUploadLog', { type })
  if (results.status === 'success')
    yield put(success({ uploadLog: results.data, typeLoading: type }))
}

function* emailCorporateTransactionHistory({ type, fromDate, toDate }) {
  const results = yield call(
    boiler,
    'emailCorporateTransactionHistory',
    { type },
    fromDate,
    toDate
  )
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* createItinerary({ type, data, trip_id }) {
  const results = yield call(boiler, 'createItinerary', { type }, trip_id, data)
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* removeItinerary({ type, trip_id, itinerary_id }) {
  const results = yield call(
    boiler,
    'removeItinerary',
    { type },
    trip_id,
    itinerary_id
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* updateItinerary({ type, trip_id, itinerary_id, base_itin, data }) {
  const results = yield call(
    boiler,
    base_itin ? 'updateBaseItinerary' : 'updateItinerary',
    { type },
    trip_id,
    itinerary_id,
    data
  )
  if (results.status === 'success')
    yield call(getTrip, { id: trip_id })
    yield put(success({ typeLoading: type }))
}

function* moveItinerary({ type, from_trip_id, to_trip_id, itinerary_id }) {
  const results = yield call(
    boiler,
    'moveItinerary',
    { type },
    from_trip_id,
    to_trip_id,
    itinerary_id
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* updateTrip({ type, data, trip_id }) {
  const results = yield call(boiler, 'updateTrip', { type }, trip_id, data)
  if (results.status === 'success') yield call(getTrip, { id: trip_id })
  yield put(success({ typeLoading: type }))
}

function* deleteTrip({ type, data, trip_id }) {
  const results = yield call(boiler, 'deleteTrip', { type }, trip_id, data)
  if (results.status === 'success') {
    yield fork(getMyTrips, {})
    yield fork(getPastTrips, {})
    yield put(success({ typeLoading: type }))
  }
}

function* createTrip({ type, data, cb }) {
  const results = yield call(boiler, 'createTrip', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
    cb?.(results.data)
  }
}

function* mergeTrips({ type, from_trip_id, to_trip_id }) {
  const results = yield call(
    boiler,
    'mergeTrips',
    { type },
    from_trip_id,
    to_trip_id
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* getDestinationCategories({ type }) {
  const results = yield call(boiler, 'getDestinationCategories', { type })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: type,
        destinationCategories: results.data,
      })
    )
}

function* getTravelCategories({ type, sub_type }) {
  const results = yield call(boiler, 'getTravelCategories', { type }, sub_type)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, travelCategories: results.data })
    )
}

function* requestItineraryCancellation({ type, trip_id, itinerary_id }) {
  const results = yield call(
    boiler,
    'requestItineraryCancellation',
    { type },
    trip_id,
    itinerary_id
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* getAllBudgets() {
  const results = yield call(boiler, 'getAllBudgets', {
    type: T.GET_ALL_BUDGETS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_ALL_BUDGETS,
        budgets: results.data,
      })
    )
}

function* getMyBudgets() {
  const results = yield call(boiler, 'getMyBudgets', {
    type: T.GET_MY_BUDGETS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_MY_BUDGETS,
        myBudgets: results.data,
      })
    )
}

function* getManagerDetails() {
  const results = yield call(boiler, 'getManagerDetails', {
    type: T.GET_MANAGER_DETAILS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_MANAGER_DETAILS,
        managerDetails: results.data,
      })
    )
}

function* updateBudget({ type, budget_id, data }) {
  const results = yield call(boiler, 'updateBudget', { type }, budget_id, data)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    if (user?.is_manager) {
      yield fork(getMyBudgets)
      yield fork(getManagerDetails)
    }
    yield fork(getAllBudgets)
    yield put(success({ typeLoading: type, budget: results.data }))
  }
}

function* getBudget({ budget_id, changes }) {
  const results = yield call(
    boiler,
    'getBudget',
    { type: T.GET_BUDGET },
    budget_id,
    changes
  )
  if (results.status === 'success')
    yield put(
      success({ typeLoading: T.GET_BUDGET, budget: results.data })
    )
}

function* createBudget({ type, data }) {
  const results = yield call(boiler, 'createBudget', { type }, data)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    if (user?.is_manager) {
      yield fork(getMyBudgets)
      yield fork(getManagerDetails)
    }
    yield fork(getAllBudgets)
    yield put(success({ typeLoading: type }))
  }
}

function* createBulkBudget({ type, data }) {
  const results = yield call(boiler, 'createBulkBudget', { type }, data)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    if (user?.is_manager) {
      yield fork(getMyBudgets)
      yield fork(getManagerDetails)
    }
    yield fork(getAllBudgets)
    yield put(success({ typeLoading: type }))
  }
}

function* deleteBudget({ type, budget_id }) {
  const results = yield call(boiler, 'deleteBudget', { type }, budget_id)
  if (results.status === 'success') {
    const { user } = yield select(getGlobalState)
    if (user?.is_manager) {
      yield fork(getMyBudgets)
      yield fork(getManagerDetails)
    }
    yield fork(getAllBudgets)
    yield put(success({ typeLoading: type }))
  }
}

function* rewardManagerToEmployee({ type, data }) {
  const results = yield call(boiler, 'rewardManagerToEmployee', { type }, data)
  if (results.status === 'success') {
    yield fork(getMyBudgets)
    yield fork(getTeam)
    yield fork(getManagerDetails)
    yield put(success({ typeLoading: type }))
  }
}

function* bulkRewardManagerToEmployee({ type, data }) {
  const results = yield call(
    boiler,
    'bulkRewardManagerToEmployee',
    { type },
    data
  )
  if (results.status === 'success') {
    yield fork(getMyBudgets)
    yield fork(getTeam)
    yield fork(getManagerDetails)
    yield put(success({ typeLoading: type }))
  }
}

function* getBudgetHistory({ search }) {
  const results = yield call(
    boiler,
    'getBudgetHistory',
    { type: T.GET_BUDGET_HISTORY },
    search
  )
  if (results.status === 'success') {
    yield put(
      success({
        budgetHistory: results.data,
        typeLoading: T.GET_BUDGET_HISTORY,
      })
    )
  }
}

function* requestAdditionalBudget({ type, budget_id, data }) {
  const results = yield call(
    boiler,
    'requestAdditionalBudget',
    { type },
    budget_id,
    data
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* requestBudget({ type, data }) {
  const results = yield call(boiler, 'requestBudget', { type }, data)
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* createMergeLinkToken({ type }) {
  const results = yield call(boiler, 'createMergeLinkToken', { type })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: type,
        mergeLinkToken: results.data.link_token,
      })
    )
}

function* getMergeAccessToken({ type, public_token }) {
  const results = yield call(
    boiler,
    'getMergeAccessToken',
    { type },
    public_token
  )
  if (results.status === 'success') {
    yield call(getHRISAccounts)
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* getRewardLog({ type }) {
  const results = yield call(boiler, 'getRewardLog', { type })
  if (results.status === 'success')
    yield put(success({ rewardLog: results.data, typeLoading: type }))
}

function* getCompanyImages() {
  const results = yield call(boiler, 'getCompanyImages', {
    type: T.GET_COMPANY_IMAGES,
  })
  if (results.status === 'success')
    yield put(
      success({
        companyImages: results.data,
        typeLoading: T.GET_COMPANY_IMAGES,
      })
    )
}

function* addCompanyImage({ type, data }) {
  const results = yield call(boiler, 'addCompanyImage', { type }, data)
  if (results.status === 'success') {
    const { companyImages = [] } = yield select(getGlobalState)
    yield put(
      success({
        companyImages: [...companyImages, results.data],
        typeLoading: type,
      })
    )
  }
}

function* bulkAddCompanyImages({ type, data }) {
  const results = yield call(boiler, 'bulkAddCompanyImages', { type }, data)
  if (results.status === 'success') {
    const { companyImages = [] } = yield select(getGlobalState)
    yield put(
      success({
        companyImages: [...results.data, ...companyImages],
        typeLoading: type,
      })
    )
  }
}

function* flagCompanyImage({ type, unique_id, flag }) {
  const results = yield call(
    boiler,
    'flagCompanyImage',
    { type },
    unique_id,
    flag
  )
  if (results.status === 'success') {
    yield call(getCompanyImages)
    yield put(success({ typeLoading: type }))
    // const { companyImages = [] } = yield select(getGlobalState)
    // yield put(success({ companyImages: [...companyImages, { image_url }], typeLoading: type }))
  }
}

function* removeCompanyImage({ type, unique_id }) {
  const results = yield call(boiler, 'removeCompanyImage', { type }, unique_id)
  if (results.status === 'success') {
    yield call(getCompanyImages)
    yield put(success({ typeLoading: type }))
    // const { companyImages = [] } = yield select(getGlobalState)
    // yield put(success({ companyImages: [...companyImages, { image_url }], typeLoading: type }))
  }
}

function* bulkRemoveCompanyImages({ type, data }) {
  const results = yield call(boiler, 'bulkRemoveCompanyImages', { type }, data)
  if (results.status === 'success') {
    yield call(getCompanyImages)
    yield put(success({ typeLoading: type }))
    // const { companyImages = [] } = yield select(getGlobalState)
    // yield put(success({ companyImages: [...companyImages, { image_url }], typeLoading: type }))
  }
}

function* getCustomerLocations({ type }) {
  const results = yield call(boiler, 'getCustomerLocations', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, customerLocations: results.data })
    )
}

function* getVotedLocations() {
  const results = yield call(boiler, 'getVotedLocations', {
    type: T.GET_VOTED_LOCATIONS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_VOTED_LOCATIONS,
        votedLocations: results.data,
      })
    )
}

function* voteLocations({ type, data }) {
  const results = yield call(boiler, 'voteLocations', { type }, data)
  if (results.status === 'success') {
    yield call(getVotedLocations)
    yield put(success({ typeLoading: type }))
  }
}

function* getCompanySettings() {
  const results = yield call(boiler, 'getCompanySettings', {
    type: T.GET_COMPANY_SETTINGS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_COMPANY_SETTINGS,
        companySettings: results.data,
      })
    )
}

function* updateCompanySettings({ type, data }) {
  const results = yield call(boiler, 'updateCompanySettings', { type }, data)
  if (results.status === 'success') {
    yield call(getCompanySettings)
    yield put(success({ typeLoading: type }))
  }
}

function* getCompanyPTOExplainer() {
  const results = yield call(boiler, 'getCompanyPTOExplainer', {
    type: T.GET_COMPANY_PTO_EXPLAINER,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_COMPANY_PTO_EXPLAINER,
        companyPTOExplainer: results.data,
      })
    )
}

function* updateCompanyPTOExplainer({ type, data }) {
  const results = yield call(
    boiler,
    'updateCompanyPTOExplainer',
    { type },
    data
  )
  if (results.status === 'success') {
    yield call(getCompanyPTOExplainer)
    yield put(success({ typeLoading: type }))
  }
}

function* getUserSettings() {
  const results = yield call(boiler, 'getUserSettings', {
    type: T.GET_USER_SETTINGS,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_USER_SETTINGS,
        userSettings: results.data,
      })
    )
}

function* updateUserSettings({ type, data }) {
  const results = yield call(boiler, 'updateUserSettings', { type }, data)
  if (results.status === 'success') {
    yield call(getUserSettings)
    yield put(success({ typeLoading: type }))
  }
}

function* getTravelCuriousPromotedCities({ type }) {
  const results = yield call(boiler, 'getTravelCuriousPromotedCities', { type })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: type,
        travelCuriousPromotedCities: results.data,
      })
    )
}

function* getTravelCuriousCities({ type }) {
  const results = yield call(boiler, 'getTravelCuriousCities', { type })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: type,
        travelCuriousCities: orderBy(results.data?.cities, o => o.name),
      })
    )
}

function* getTravelCuriousTours({ type, params }) {
  const results = yield call(boiler, 'getTravelCuriousTours', { type }, params)
  if (results.status === 'success') {
    if (params.includes('tour_id')) {
      yield put(
        success({
          typeLoading: type,
          tour: results.data.tours?.[0] || {},
        })
      )
    } else {
      yield put(
        success({ typeLoading: type, travelCuriousTours: results.data })
      )
    }
  }
}

function* bookTravelCurious({ type, data }) {
  const results = yield call(boiler, 'bookTravelCurious', { type }, data)
  if (results.status === 'success') {
    yield put(
      success({ typeLoading: type, orderConfirmation: results.data })
    )
  }
}

function* requestViatorVC({ type, data }) {
  const results = yield call(boiler, 'requestViatorVC', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type, orderConfirmation: results.data, virtualCardConfirmation: results.data }))
  }
}

function* setEmailCommunicationPreferences({ type, data }) {
  const results = yield call(
    boiler,
    'setEmailCommunicationPreferences',
    { type },
    data
  )
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
  }
}

function* bulkAddTripImages({ type, data, trip_id }) {
  const results = yield call(
    boiler,
    'bulkAddTripImages',
    { type },
    trip_id,
    data
  )
  if (results.status === 'success') {
    yield call(getTrip, { id: trip_id })
    yield put(success({ typeLoading: type }))
  }
}

function* bulkRemoveTripImages({ type, data, trip_id }) {
  const results = yield call(
    boiler,
    'bulkRemoveTripImages',
    { type },
    trip_id,
    data
  )
  if (results.status === 'success') {
    yield call(getTrip, { id: trip_id })
    yield put(success({ typeLoading: type }))
  }
}

function* getCompanyActivity({ type }) {
  const results = yield call(boiler, 'getCompanyActivity', { type })
  if (results.status === 'success') {
    yield put(
      success({ typeLoading: type, companyActivity: results.data })
    )
  }
}

function* getFirstSyncStatus({ type, account_id }) {
  const results = yield call(boiler, 'getFirstSyncStatus', { type }, account_id)
  if (results.status === 'success') {
    if (results.data.status === HRIS_SYNC_STATUS.COMPLETE) {
      const { hrisAccounts = [] } = yield select(getGlobalState)
      const account = hrisAccounts[0] || {}
      if (account.unique_id)
        yield call(getHRISStatus, { account_id: account.unique_id })
    }
    yield put(
      success({ typeLoading: type, firstSyncStatus: results.data })
    )
  }
}

function* getHRISAccounts() {
  const results = yield call(boiler, 'getHRISAccounts', {
    type: T.UPDATE_HRIS_ELIGIBILITY,
  })
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.UPDATE_HRIS_ELIGIBILITY,
        hrisAccounts: results.data,
      })
    )
  }
}

function* updateHRISAccount({ type, account_id, data }) {
  const results = yield call(
    boiler,
    'updateHRISAccount',
    { type },
    account_id,
    data
  )
  if (results.status === 'success') {
    yield call(getHRISAccounts)
    yield put(success({ typeLoading: type }))
  }
}

function* removeHRISAccount({ type, account_id }) {
  const results = yield call(boiler, 'removeHRISAccount', { type }, account_id)
  if (results.status === 'success') {
    yield call(getHRISAccounts)
    yield put(
      success({
        typeLoading: type,
        hrisAccounts: undefined,
        firstSyncStatus: undefined,
        hrisEligibility: undefined,
        hrisStatusCounts: undefined,
        eligibleEmployees: undefined,
        unmergedEmployees: undefined,
        hrisEmployeeSearchResults: undefined,
        hrisStatus: undefined,
      })
    )
  }
}

function* getHRISEligibility() {
  const results = yield call(boiler, 'getHRISEligibility', {
    type: T.GET_HRIS_ELIGIBILITY,
  })
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.GET_HRIS_ELIGIBILITY,
        hrisEligibility: results.data,
      })
    )
  }
}

function* updateHRISEligibility({ type, data, account_id }) {
  const results = yield call(boiler, 'updateHRISEligibility', { type }, data)
  if (results.status === 'success') {
    if (account_id) yield call(getHRISStatus, { account_id })
    yield put(
      success({ typeLoading: type, hrisEligibility: results.data })
    )
  }
}

function* confirmEligibilityRules({ type, data, account_id }) {
  const results = yield call(boiler, 'confirmEligibilityRules', { type }, data)
  if (results.status === 'success') {
    if (account_id) yield call(getHRISStatus, { account_id })
    yield put(success({ typeLoading: type }))
  }
}

function* getHRISStatusCounts() {
  const results = yield call(boiler, 'getHRISStatusCounts', {
    type: T.GET_HRIS_STATUS_COUNTS,
  })
  if (results.status === 'success') {
    yield call(getCompany)
    yield put(
      success({
        typeLoading: T.GET_HRIS_STATUS_COUNTS,
        hrisStatusCounts: results.data,
      })
    )
  }
}

function* processEligibleEmployees({ type, account_id }) {
  const results = yield call(
    boiler,
    'processEligibleEmployees',
    { type },
    account_id
  )
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(
      success({ typeLoading: type, eligibleEmployees: results.data })
    )
  }
}

function* overrideEmployeeEligibility({ type, employee_id }) {
  const results = yield call(
    boiler,
    'overrideEmployeeEligibility',
    { type },
    employee_id
  )
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* overrideEmployeeIneligibility({ type, employee_id }) {
  const results = yield call(
    boiler,
    'overrideEmployeeIneligibility',
    { type },
    employee_id
  )
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* confirmIneligibility({ type, employee_id }) {
  const results = yield call(
    boiler,
    'confirmIneligibility',
    { type },
    employee_id
  )
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* getUnmergedEmployees({ type }) {
  const results = yield call(boiler, 'getUnmergedEmployees', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, unmergedEmployees: results.data })
    )
}

function* searchHRISEmployees({ type, search }) {
  const results = yield call(boiler, 'searchHRISEmployees', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({
        hrisEmployeeSearchResults: results.data,
        typeLoading: type,
      })
    )
  } else {
    yield put(
      success({ hrisEmployeeSearchResults: [], typeLoading: type })
    )
  }
}

function* getPaginatedHRISRecords({
  type,
  page_size,
  page,
  filters,
  is_eligible,
  awaiting_invite,
}) {
  const results = yield call(
    boiler,
    'getPaginatedHRISRecords',
    { type },
    page_size,
    page,
    filters,
    is_eligible,
    awaiting_invite
  )
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, hrisRecords: results.data }))
  }
}

function* mergeHRISEmployee({
  type,
  donde_user_unique_id,
  hris_employee_unique_id,
}) {
  const results = yield call(
    boiler,
    'mergeHRISEmployee',
    { type },
    donde_user_unique_id,
    hris_employee_unique_id
  )
  if (results.status === 'success') {
    // yield call(getCompany)
    yield put(success({ typeLoading: type }))
  }
}

function* deactivateHRISEmployee({ type, donde_user_unique_id }) {
  const results = yield call(
    boiler,
    'deactivateHRISEmployee',
    { type },
    donde_user_unique_id
  )
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* getHRISStatus({ account_id }) {
  const results = yield call(
    boiler,
    'getHRISStatus',
    { type: T.GET_HRIS_STATUS },
    account_id
  )
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.GET_HRIS_STATUS,
        hrisStatus: results.data,
      })
    )
  }
}

function* sendHRISEmployeeInvites({ type, data }) {
  const results = yield call(boiler, 'sendHRISEmployeeInvites', { type }, data)
  if (results.status === 'success') {
    yield call(getHRISStatusCounts)
    yield put(success({ typeLoading: type }))
  }
}

function* getCollections() {
  const results = yield call(boiler, 'getCollections', {
    type: T.GET_COLLECTIONS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.GET_COLLECTIONS,
        collections: results.data,
      })
    )
  }
}

function* getAllCollections() {
  const results = yield call(boiler, 'getAllCollections', {
    type: T.GET_ALL_COLLECTIONS,
  })
  if (results.status === 'success') {
    yield put(
      success({
        typeLoading: T.GET_ALL_COLLECTIONS,
        allCollections: results.data,
      })
    )
  }
}

function* getCollection({ collection_id }) {
  const results = yield call(boiler, 'getCollection', { type: T.GET_COLLECTION }, collection_id)
  if (results.status === 'success') {
    yield put(success({ typeLoading: T.GET_COLLECTION, collection: results.data }))
  }
}

function* changeCollectionName({ type, collection_id, name }) {
  const results = yield call(boiler, 'changeCollectionName', { type }, collection_id, name)
  if (results.status === 'success') {
    yield fork(getCollection, { collection_id })
    yield put(success({ typeLoading: type }))
  }
}

function* createCollection({ type, name }) {
  const results = yield call(boiler, 'createCollection', { type }, name)
  if (results.status === 'success') {
    yield fork(getCollections)
    yield fork(getAllCollections)
    yield put(success({ typeLoading: type }))
  }
}

function* deleteCollection({ type, collection_id }) {
  const results = yield call(
    boiler,
    'deleteCollection',
    { type },
    collection_id
  )
  if (results.status === 'success') {
    yield fork(getCollections)
    yield fork(getAllCollections)
    yield put(success({ typeLoading: type }))
  }
}

function* mergeCollections({ type, from_collection_id, to_collection_id }) {
  const results = yield call(
    boiler,
    'mergeCollections',
    { type },
    from_collection_id,
    to_collection_id
  )
  if (results.status === 'success') {
    yield fork(getCollections)
    yield fork(getAllCollections)
    yield put(success({ typeLoading: type }))
  }
}

function* connectCollectionToTrip({ type, collection_id, trip_id }) {
  const results = yield call(
    boiler,
    'connectCollectionToTrip',
    { type },
    collection_id,
    trip_id
  )
  if (results.status === 'success') {
    yield fork(getCollection, { collection_id })
    yield fork(getTrip, { id: trip_id })
    yield put(success({ typeLoading: type }))
  }
}

function* disconnectCollectionFromTrip({ type, collection_id, trip_id }) {
  const results = yield call(
    boiler,
    'disconnectCollectionFromTrip',
    { type },
    collection_id,
    trip_id
  )
  if (results.status === 'success') {
    yield fork(getCollection, { collection_id })
    yield fork(getTrip, { id: trip_id })
    yield put(success({ typeLoading: type }))
  }
}

function* getFavorites() {
  const results = yield call(boiler, 'getFavorites', {
    type: T.GET_FAVORITES,
  })
  if (results.status === 'success')
    yield put(
      success({
        typeLoading: T.GET_FAVORITES,
        favorites: results.data,
      })
    )
}

function* addFavorite({ type, content_id, vendor }) {
  const results = yield call(boiler, 'addFavorite', { type }, content_id, vendor)
  if (results.status === 'success') {
    yield call(getFavorites)
    const { favorites } = yield select(getGlobalState)
    const collectionID = favorites.unique_id
    yield put(
      success({
        typeLoading: type,
        [`success${type}`]: { isFirstFavorite: !!!collectionID },
        recentlyAddedContent: { collectionID },
      })
    )
  }
}

function* removeFavorite({ type, content_id }) {
  const results = yield call(boiler, 'removeFavorite', { type }, content_id)
  if (results.status === 'success') {
    yield fork(getFavorites)
    yield put(success({ typeLoading: type }))
  }
}

function* addContentToCollection({
  type,
  collection_id,
  content_id,
  collectionName,
  vendor,
}) {
  const results = yield call(
    boiler,
    'addContentToCollection',
    { type },
    collection_id,
    content_id,
    vendor,
  )
  if (results.status === 'success') {
    yield fork(getCollections)
    yield put(
      success({
        typeLoading: type,
        recentlyAddedContent: collectionName
          ? { collectionID: collection_id, collectionName }
          : null,
      })
    )
  }
}

function* removeContentFromCollection({ type, collection_id, content_id }) {
  const results = yield call(
    boiler,
    'removeContentFromCollection',
    { type },
    collection_id,
    content_id
  )
  if (results.status === 'success') {
    yield fork(getCollection, { collection_id })
    yield put(success({ typeLoading: type }))
  }
}

function* getRecentContent({ type }) {
  const results = yield call(boiler, 'getRecentContent', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, recentContent: results.data })
    )
}

function* getBookingStats({ type, num_of_months }) {
  const results = yield call(boiler, 'getBookingStats', { type }, num_of_months)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, bookingStats: results.data })
    )
}

function* getBudgetStats({ type }) {
  const results = yield call(boiler, 'getBudgetStats', { type })
  if (results.status === 'success')
    yield put(success({ typeLoading: type, budgetStats: results.data }))
}

function* getEmployeeStats({ type }) {
  const results = yield call(boiler, 'getEmployeeStats', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, employeeStats: results.data })
    )
}

function* getFundingStats({ type }) {
  const results = yield call(boiler, 'getFundingStats', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, fundingStats: results.data })
    )
}

function* getEvolveMigrationSetting({ type }) {
  const results = yield call(boiler, 'getEvolveMigrationSetting', { type })
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, evolveMigration: results.data })
    )
}

function* getHotels({ data }) {
  const results = yield call(boiler, 'getHotels', { type: T.GET_HOTELS }, data)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: T.GET_HOTELS, hotels: results.data })
    )
}

function* searchNearbyStays({ data, ...rest }) {
  const results = yield call(boiler, 'getHotels', { type: T.SEARCH_NEARBY_STAYS }, data)
  if (results.status === 'success')
    yield put(
      success({ 
        typeLoading: T.SEARCH_NEARBY_STAYS, 
        [`success${T.SEARCH_NEARBY_STAYS}`]: { hasResults: !isEmpty(results.data?.hotels) },
        nearbyStays: {
          ...results.data,
          ...rest,
        }, 
      })
    )
}

function* getHotelAmenities({ type, data }) {
  const results = yield call(boiler, 'getHotelAmenities', { type }, data)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, hotelAmenities: results.data })
    )
}

function* getHotel({ type, hotel_id }) {
  const results = yield call(boiler, 'getHotel', { type }, hotel_id)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, hotel: results.data })
    )
}

function* getHotelAvailability({ type, hotel_id, data }) {
  const results = yield call(boiler, 'getHotelAvailability', { type }, hotel_id, data)
  if (results.status === 'success')
    yield put(
      success({ typeLoading: type, hotelAvailability: results.data })
    )
}

function* bookHotelPlanner({ type, data }) {
  const results = yield call(boiler, 'bookHotelPlanner', { type }, data)
  yield put(clearFromState(['hotelOrderNumber', 'hotelReservationStatus']))
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, hotelOrderNumber: results.data }))
    yield fork(getSavingsTotals)
  }
}

function* getHotelPlannerReservationStatus({ type, reservation_id }) {
  const results = yield call(boiler, 'getHotelPlannerReservationStatus', { type }, reservation_id)
  if (results.status === 'success') yield put(success({ typeLoading: type, hotelReservationStatus: results.data }))
}

function* requestHotelPlannerCancellation({ type, reservation_id, itinerary_number }) {
  const results = yield call(
    boiler,
    'requestHotelPlannerCancellation',
    { type },
    reservation_id,
    itinerary_number
  )
  if (results.status === 'success')
    yield put(success({ typeLoading: type }))
}

function* getRandomDestinations({ type }) {
  const results = yield call(boiler, 'getRandomDestinations', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, randomDestinations: results.data }))
}

function* getSearchHistory() {
  const results = yield call(boiler, 'getSearchHistory', { type: T.GET_SEARCH_HISTORY })
  if (results.status === 'success') yield put(success({ typeLoading: T.GET_SEARCH_HISTORY, searchHistory: results.data }))
}

function* addToSearchHistory({ type, data, hash }) {
  const results = yield call(boiler, 'addToSearchHistory', { type }, data, hash)
  if (results.status === 'success') {
    yield fork(getSearchHistory)
    yield put(success({ typeLoading: type }))
  }
}

function* generalError({ error_stack }) {
  // if (!IS_DEV) {
  if (!window.location.hostname.includes('dev.local.me')) {
    const results = yield call(boiler, 'generalError', { type: T.GENERAL_ERROR }, error_stack)
    if (results.status === 'success') yield put(success({ typeLoading: T.GENERAL_ERROR }))
  }
}

function* getMatchHistory({ type }) {
  const results = yield call(boiler, 'getMatchHistory', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, matchHistory: results.data }))
}

function* getPayrollAccessToken({ type, uniqueID }) {
  const results = yield call(boiler, 'getPayrollAccessToken', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, payrollAccessToken: { ...results.data, uniqueID } }))
}

function* requestAdminPayrollDisconnect({ type }) {
  const results = yield call(boiler, 'requestAdminPayrollDisconnect', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* setPayrollDisconnectRequest({ type, requested }) {
  const results = yield call(boiler, 'setPayrollDisconnectRequest', { type }, requested)
  if (results.status === 'success') {
    yield call(getContributionSettings)
    yield put(success({ typeLoading: type }))
  }
}

function* removePayroll({ type }) {
  const results = yield call(boiler, 'removePayroll', { type })
  if (results.status === 'success') {
    yield call(getContributionSettings)
    yield put(success({ typeLoading: type }))
  }
}

function* requestPartnerVC({ type, data }) {
  const results = yield call(boiler, 'requestPartnerVC', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type, virtualCardConfirmation: results.data }))
  }
}

function* requestFlightVC({ type, data }) {
  const results = yield call(boiler, 'requestFlightVC', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type, virtualCardConfirmation: results.data }))
  }
}

function* getMyRewards({ type }) {
  const results = yield call(boiler, 'getMyRewards', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, myRewards: results.data }))
}

function* getInsuranceProducts({ type }) {
  const results = yield call(boiler, 'getInsuranceProducts', { type, errorTitle: 'Notice', errorDismiss: true })
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceProducts: sortBy(results.data, { is_single_trip: false }) }))
}

function* getInsuranceProductStates({ type, productID, countryID }) {
  const results = yield call(boiler, 'getInsuranceProductStates', { type, errorTitle: 'Notice', errorDismiss: true }, productID, countryID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceProductStates: results.data }))
}

function* getInsuranceProductsHostCountries({ type, productID }) {
  const results = yield call(boiler, 'getInsuranceProductsHostCountries', { type, errorTitle: 'Notice', errorDismiss: true }, productID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceProductHostCountries: results.data }))
}

function* getInsuranceProductsDestinationCountries({ type, productID }) {
  const results = yield call(boiler, 'getInsuranceProductsDestinationCountries', { type, errorTitle: 'Notice', errorDismiss: true }, productID)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, insuranceProductDestinationCountries: results.data.filter(o => !EXCLUDED_DESTINATIONS.includes(o.name)) }))
  }
}

function* getInsuranceProductsByState({ type, productID, countryID, stateID }) {
  const results = yield call(boiler, 'getInsuranceProductsByState', { type, errorTitle: 'Notice', errorDismiss: true }, productID, countryID, stateID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceProductsByState: results.data }))
}

function* getInsuranceUpfrontQuote({ type, productID, data }) {
  const results = yield call(boiler, 'getInsuranceUpfrontQuote', { type, errorTitle: 'Notice', errorDismiss: true }, productID, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceUpfrontQuote: results.data }))
}

function* getInsuranceFinalQuote({ type, productID, data }) {
  const results = yield call(boiler, 'getInsuranceFinalQuote', { type, errorTitle: 'Notice', errorDismiss: true }, productID, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceFinalQuote: results.data }))
}

function* bookInsurance({ type, productID, data }) {
  const results = yield call(boiler, 'bookInsurance', { type, errorTitle: 'Notice' }, productID, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, insuranceOrder: results.data }))
}

function* getInsurancePayment({ type, productID }) {
  const results = yield call(boiler, 'getInsurancePayment', { type, errorTitle: 'Notice' }, productID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insurancePayment: results.data }))
}

function* getInsurancePolicyFiles({ type, policyID }) {
  const results = yield call(boiler, 'getInsurancePolicyFiles', { type, errorTitle: 'Notice' }, policyID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insurancePolicyFiles: results.data }))
}

function* getInsurancePolicyTrip({ type, productID }) {
  const results = yield call(boiler, 'getInsurancePolicyTrip', { type, errorTitle: 'Notice' }, productID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insurancePolicyTrip: results.data }))
}

function* getInsurancePolicyDetails({ type, policyID }) {
  const results = yield call(boiler, 'getInsurancePolicyDetails', { type, errorTitle: 'Notice' }, policyID)
  if (results.status === 'success') yield put(success({ typeLoading: type, insurancePolicyDetails: results.data }))
}

function* connectPolicyToTrip({ type, policyID, tripID }) {
  const results = yield call(boiler, 'connectPolicyToTrip', { type }, policyID, tripID)
  if (results.status === 'success') {
    yield fork(getMyTrips, {})
    yield put(success({ typeLoading: type }))
  }
}

function* removePolicyFromTrip({ type, productID, tripID }) {
  const results = yield call(boiler, 'removePolicyFromTrip', { type }, productID, tripID)
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* getPolicies({ type, quotes_only, orders_only }) {
  const results = yield call(boiler, 'getPolicies', { type }, quotes_only, orders_only)
  if (results.status === 'success') {
    yield put(success({ 
      typeLoading: type, 
      [`policy${quotes_only ? 'Quotes' : ''}${orders_only ? 'Orders' : ''}`]: results.data,
    }))
  }
}

function* createKYBBusiness({ type, legal_business_name }) {
  const results = yield call(boiler, 'createKYBBusiness', { type }, legal_business_name)
  if (results.status === 'success') yield put(success({ typeLoading: type, kybBusiness: results.data }))
}

function* getKYBInquiryID({ type }) {
  const results = yield call(boiler, 'getKYBInquiryID', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, kybInquiryID: results.data }))
}

function* setKYBUnderReview({ type }) {
  const results = yield call(boiler, 'setKYBUnderReview', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* requestFundWithdrawal({ type, data }) {
  const results = yield call(boiler, 'requestFundWithdrawal', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* getMaxWithdrawal({ type }) {
  const results = yield call(boiler, 'getMaxWithdrawal', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type, maxWithdrawal: results.data }))
}

function* getUserStatementPDF({ type, month, year }) {
  const results = yield call(boiler, 'getUserStatementPDF', { type }, month, year)
  if (results.status === 'success') {
    const blob = yield call(() => results.data.blob())
    yield put(success({ typeLoading: type, blobDownload: blob }))
  }
}

function* getCorporateStatementPDF({ type, month, year }) {
  const results = yield call(boiler, 'getCorporateStatement', { type }, month, year)
  if (results.status === 'success') yield put(success({ typeLoading: type, blobDownload: results.data }))
}

function* getCorporateStatement({ type, month, year }) {
  const results = yield call(boiler, 'getCorporateStatement', { type }, month, year)
  if (results.status === 'success') yield put(success({ typeLoading: type, corporateStatement: results.data }))
}

function* getBankAuthorization({ type, agreement_type }) {
  const results = yield call(boiler, 'getBankAuthorization', { type }, agreement_type)
  if (results.status === 'success') yield put(success({ typeLoading: type, [agreement_type]: results.data?.agreements }))
}

function* setMigrationStatus({ type, state, balance }) {
  const results = yield call(boiler, 'setMigrationStatus', { type }, state, balance)
  if (results.status === 'success') {
    yield call(getUser)
    yield put(success({ typeLoading: type, migrationStatus: results.data }))
  }
}

function* checkPendingTransactions({ type }) {
  const results = yield call(boiler, 'setMigrationStatus', { type }, 'pending_transactions')
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, hasPendingTransactions: results.data }))
  }
}

function* getAffiliates({ type, tags, selector = 'affiliates' }) {
  const results = yield call(boiler, 'getAffiliates', { type }, tags)
  if (results.status === 'success') yield put(success({ typeLoading: type, [selector]: results.data }))
}

function* getOffers({ type, tags, selector = 'offers' }) {
  const results = yield call(boiler, 'getOffers', { type }, tags)
  if (results.status === 'success') yield put(success({ typeLoading: type, [selector]: results.data }))
}

function* getFlightOffers({ type }) {
  const results = yield call(boiler, 'getOffers', { type }, ['shop_travel'])
  if (results.status === 'success') yield put(success({ typeLoading: type, flightOffers: results.data }))
}

function* requestAffiliateVCC({ type, data }) {
  const results = yield call(boiler, 'requestAffiliateVCC', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type }))
  }
}

function* requestGeneralVC({ type, data }) {
  const results = yield call(boiler, 'requestGeneralVC', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type, virtualCardConfirmation: results.data }))
  }
}

function* sendAmountDisputeTicket({ type }) {
  const results = yield call(boiler, 'sendAmountDisputeTicket', { type })
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* getGeneralSetting({ type, setting }) {
  const results = yield call(boiler, 'getGeneralSetting', { type }, setting)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, [`${setting}_setting`]: results.data?.value === 'True' }))
  }
}

function* getDestinationImage({ type, data, imageRef }) {
  const results = yield call(boiler, 'getDestinationImage', { type }, data)
  if (results.status === 'success') {
    const { destinationImages } = yield select(getGlobalState)
    yield put(success({ 
      typeLoading: type, 
      destinationImages: { ...destinationImages, [imageRef]: results.data },
    }))
  }
}
  
function* getVirtualCards({ type, status }) {
  const results = yield call(boiler, 'getVirtualCards', { type }, status)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, virtualCards: results.data }))
  }
}

function* getVirtualCard({ unique_id }) {
  const results = yield call(boiler, 'getVirtualCard', { type: T.GET_VIRTUAL_CARD }, unique_id)
  if (results.status === 'success') {
    yield put(success({ typeLoading: T.GET_VIRTUAL_CARD, virtualCard: results.data }))
  }
}

function* getVirtualCardLink({ type, unique_id }) {
  const results = yield call(boiler, 'getVirtualCardLink', { type }, unique_id)
  if (results.status === 'success') {
    yield put(success({ 
      typeLoading: type, 
      virtualCardLink: results.data,
      [`success${type}`]: { virtualCardLinkID: unique_id, virtualCardLink: results.data?.link },
    }))
  }
}

function* cancelVirtualCard({ type, unique_id }) {
  const results = yield call(boiler, 'cancelVirtualCard', { type }, unique_id)
  if (results.status === 'success') {
    yield fork(getSavingsTotals)
    yield call(getVirtualCard, { unique_id })
    yield put(success({ typeLoading: type }))
  }
}

function* reportVirtualCardIssue({ type, unique_id, data }) {
  const results = yield call(boiler, 'reportVirtualCardIssue', { type }, unique_id, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
  }
}

function* getAIModels({ type }) {
  const results = yield call(boiler, 'getAIModels', { type })
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, aiModels: results.data?.data }))
  }
}

function* getAIModel({ type, modelID }) {
  const results = yield call(boiler, 'getAIModel', { type }, modelID)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, aiModel: results.data }))
  }
}

function* sendAIChat({ type, selector, modelID, messages, cb, ...rest }) {
  yield put(clearFromState([selector]))
  const results = yield call(boiler, 'sendAIChat', { type }, { model: modelID, messages, ...rest })
  if (results.status === 'success') {
    const message = results.data?.choices?.[0]?.message
    const args = message?.function_call?.arguments
    const response = rest.function_call === 'auto' || !rest.function_call ? message : args?.length ? JSON.parse(args) : {}
    cb?.(response)
    yield put(success({ 
      typeLoading: type, 
      [selector]: response,
    }))
  }
}

function* sendAICompletion({ type, modelID, prompt, max_tokens = 128, ...rest }) {
  const results = yield call(boiler, 'sendAICompletion', { type }, { model: modelID, prompt, max_tokens, ...rest })
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, aiCompletion: results.data }))
  }
}

function* getAIFineTune({ type, modelID }) {
  const results = yield call(boiler, 'getAIFineTune', { type }, modelID)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, aiModel: results.data }))
  }
}

function* sendCustomerServiceRequest({ type, data }) {
  const results = yield call(boiler, 'sendCustomerServiceRequest', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
  }
}

function* requestReimbursement({ type, data }) {
  const results = yield call(boiler, 'requestReimbursement', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type }))
  }
}

function* getMonthlyDeal({ type, data }) {
  const results = yield call(boiler, 'getMonthlyDeal', { type }, data)
  if (results.status === 'success') {
    // yield call(getContentDetails, { id: results.data?.[0]?.unique_id })
    yield put(success({ typeLoading: type, monthlyDeal: results.data?.[0] || {} }))
  }
}

function* getExperiencesAutocomplete({ type, data }) {
  yield put(clearFromState(['experiencesAutocomplete']))
  const results = yield call(boiler, 'getExperiencesAutocomplete', { type }, data)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, experiencesAutocomplete: results.data?.results || [] }))
  }
}

// const removeGeneralBranches = tree => {
//   return tree
//     .map((node) => {
//       if (node.branches) {
//         node.branches = removeGeneralBranches(node.branches)
//       }
//       return node
//     })
//     .filter(
//       (node) => 
//         node.label !== "General" &&
//         (!node.branches || (node.branches && node.branches.length > 0))
//     )
// }

function* searchHolibob({ params }) {
  const results = yield call(boiler, 'searchHolibob', { type: T.SEARCH_HOLIBOB }, params)
  if (results.status === 'success') {
    if (results.data.count < 100 && params.radius !== 100) {
      const newParams = {
        ...params,
        radius: 100,
      }
      const widerResults = yield call(boiler, 'searchHolibob', { type: T.SEARCH_HOLIBOB }, newParams)
      if (results.status === 'success') {
        yield put(success({ 
          holibobExperiences: widerResults.data, 
          ...(params.category ? {} : { holibobCategoryTree: widerResults.data.category_tree }), 
          experienceRadius: 100,
          typeLoading: T.SEARCH_HOLIBOB,
        }))
      }
    } else {
      yield put(success({ 
        holibobExperiences: results.data, 
        ...(params.category ? {} : { holibobCategoryTree: results.data.category_tree }), 
        experienceRadius: params.radius || undefined,
        typeLoading: T.SEARCH_HOLIBOB,
      }))
    }
  } else {
    yield put(success({ 
      holibobExperiences: [], 
      ...(params.category ? {} : { holibobCategoryTree: [] }), 
      experienceRadius: params.radius || undefined,
      typeLoading: T.SEARCH_HOLIBOB,
    }))
  }
}

function* searchNearbyExperiences({ params }) {
  const results = yield call(boiler, 'searchHolibob', { type: T.SEARCH_NEARBY_EXPERIENCES }, params)
  if (results.status === 'success') {
    yield put(success({ 
      typeLoading: T.SEARCH_NEARBY_EXPERIENCES, 
      nearbyExperiences: results.data,
      [`success${T.SEARCH_NEARBY_EXPERIENCES}`]: { hasResults: !isEmpty(results.data?.results) },
    }))
  }
}

function* getHolibobExperienceTags({ type }) {
  const results = yield call(boiler, 'getHolibobExperienceTags', { type })
  if (results.status === 'success') {
    yield put(success({ experienceTags: results.data?.category_tree, typeLoading: type }))
  } else {
    yield put(success({ experienceTags: [], typeLoading: type }))
  }
}

function* getHolibobCategories({ type }) {
  const results = yield call(boiler, 'getHolibobCategories', { type })
  if (results.status === 'success') {
    yield put(success({ experienceCategories: results.data?.category_tree, typeLoading: type }))
  } else {
    yield put(success({ experienceCategories: [], typeLoading: type }))
  }
}

function* getHolibobPlaces({ type, search_term }) {
  const results = yield call(boiler, 'getHolibobPlaces', { type }, search_term)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, holibobPlaces: results.data }))
  }
}

function* getHolibobProductDetails({ type, productID }) {
  const results = yield call(boiler, 'getHolibobProductDetails', { type }, productID)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, holibobProductDetails: results.data }))
  }
}

function* getHolibobProductAvailabilityList({ type, productID, params }) {
  const results = yield call(boiler, 'getHolibobProductAvailabilityList', { type }, productID, params)
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, holibobProductAvailabilityList: results.data }))
  }
}

function* getHolibobProductAvailability({ availabilityID }) {
  const results = yield call(boiler, 'getHolibobProductAvailability', { type: T.GET_HOLIBOB_PRODUCT_AVAILABILITY }, availabilityID)
  if (results.status === 'success') {
    const {
      holibobProductOptions,
      holibobProductPricing,
    } = yield select(getGlobalState)
    if (holibobProductOptions?.availability?.id !== availabilityID) {
      const options = results.data?.availability?.optionList?.nodes?.map(o => ({ id: o.id, value: o.answerValue }))?.filter(o => !!o.value)
      if (options.length) {
        yield call(getHolibobProductOptions, { 
          availabilityID,
          data: { options },
          skipAvailability: true,
        })
      }
    }
    if (holibobProductPricing?.availability?.id !== availabilityID) {
      const pricing = results.data?.availability?.pricingCategoryList?.nodes?.map(o => ({ id: o.id, units: o.units }))
      // const pricing = results.data?.availability?.pricingCategoryList?.nodes?.map(o => ({ id: o.id, units: o.id === 'ADULT' ? Math.max(1, o.units) : o.units }))
      if (pricing?.length) {
        yield call(getHolibobProductPricing, { 
          availabilityID,
          data: { pricing },
          skipAvailability: true,
        })
      }
    }
    yield put(success({ typeLoading: T.GET_HOLIBOB_PRODUCT_AVAILABILITY, holibobProductAvailability: results.data }))
    // const holibobProductAvailability = {
    //   ...results.data
    // }
    // if (holibobProductAvailability?.availability?.pricingCategoryList?.nodes?.length) {
    //   holibobProductAvailability.availability.pricingCategoryList.nodes = holibobProductAvailability.availability.pricingCategoryList.nodes.map(o => {
    //     return o.id === 'ADULT' ? ({ 
    //       ...o, 
    //       units: Math.max(1, o.units), 
    //       totalPrice: { 
    //         ...o.totalPrice, 
    //         gross: Math.max(1, o.units) * o.unitPrice.gross, 
    //         grossFormattedText: `US$ ${Math.max(1, o.units) * o.unitPrice.gross}`,
    //       },
    //     }) : o
    //   })
    // }
    // yield put(success({ typeLoading: T.GET_HOLIBOB_PRODUCT_AVAILABILITY, holibobProductAvailability }))
  }
}


function* getHolibobProductOptions({ availabilityID, data, skipAvailability }) {
  const results = yield call(boiler, 'getHolibobProductOptions', { type: T.GET_HOLIBOB_PRODUCT_OPTIONS }, availabilityID, data)
  if (results.status === 'success') {
    // if (!results.data?.availability?.optionList?.isComplete) {
    // }
    if (!skipAvailability) yield call(getHolibobProductAvailability, { availabilityID })
    yield put(success({ typeLoading: T.GET_HOLIBOB_PRODUCT_OPTIONS, holibobProductOptions: results.data }))
  }
}

function* getHolibobProductPricing({ availabilityID, data, skipAvailability }) {
  const results = yield call(boiler, 'getHolibobProductPricing', { type: T.GET_HOLIBOB_PRODUCT_PRICING }, availabilityID, data)
  if (results.status === 'success') {
    if (!skipAvailability) yield call(getHolibobProductAvailability, { availabilityID })
    yield put(success({ typeLoading: T.GET_HOLIBOB_PRODUCT_PRICING, holibobProductPricing: results.data }))
    // yield put(success({ typeLoading: type, holibobProductPricing: results.data }))
  }
}

function* bookHolibob({ type, data, bookingID }) {
  const results = yield call(boiler, 'bookHolibob', { type }, data, bookingID)
  yield put(clearFromState(['experienceOrderNumber', 'experienceReservationStatus']))
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, experienceOrderNumber: results.data }))
    yield fork(getSavingsTotals)
  }
}

function* addHolibobAvailabilityToBooking({ availabilityID, bookingID }) {
  const results = yield call(boiler, 'addHolibobAvailabilityToBooking', { type: T.ADD_HOLIBOB_AVAILABILITY_TO_BOOKING }, availabilityID, bookingID)
  if (results.status === 'success') {
    yield put(success({ 
      typeLoading: T.ADD_HOLIBOB_AVAILABILITY_TO_BOOKING, 
      // holibobBooking: results.data,
    }))
    return true
  } else return false
}

function* getHolibobBooking({ bookingID, cb }) {
  const results = yield call(boiler, 'getHolibobBooking', { type: T.GET_HOLIBOB_BOOKING }, bookingID)
  if (results.status === 'success') {
    cb?.()
    yield put(success({ typeLoading: T.GET_HOLIBOB_BOOKING, holibobBooking: results.data }))
  }
}

function* createHolibobBooking({ availabilityID, cb }) {
  const results = yield call(boiler, 'createHolibobBooking', { type: T.CREATE_HOLIBOB_BOOKING })
  if (results.status === 'success') {
    const addSuccess = yield call(addHolibobAvailabilityToBooking, { 
      availabilityID,
      bookingID: results.data?.bookingCreate?.id,
    })
    if (addSuccess) yield call(getHolibobBooking, { bookingID: results.data?.bookingCreate?.id })
    if (results.data?.bookingCreate?.id && addSuccess) cb?.(results.data?.bookingCreate?.id)
    yield put(success({ 
      typeLoading: T.CREATE_HOLIBOB_BOOKING, 
      // holibobBooking: results.data,
    }))
  }
}

function* submitHolibobBookingAnswers({ type, bookingID, data }) {
  const results = yield call(boiler, 'submitHolibobBookingAnswers', { type }, bookingID, data)
  if (results.status === 'success') {
    yield call(getHolibobBooking, { bookingID })
    yield put(success({ typeLoading: type, holibobBookingAnswers: results.data }))
  }
}

function* getHolibobBookingVoucher({ type, bookingID, data, cb }) {
  const results = yield call(boiler, 'getHolibobBookingVoucher', { type }, bookingID, data)
  if (results.status === 'success') {
    if (results.data?.bookingVoucher?.url) cb?.(results.data?.bookingVoucher?.url)
    yield put(success({ typeLoading: type, holibobBookingVoucher: results.data }))
  }
}

function* requestHolibobCancellation({ type, reservationID }) {
  const results = yield call(boiler, 'requestHolibobCancellation', { type }, reservationID)
  if (results.status === 'success') yield put(success({ typeLoading: type }))
}

function* getChatbotResponse({ type, data }) {
  const results = yield call(boiler, 'getChatbotResponse', { type }, data)
  if (results.status === 'success') {
    yield put(addToChatHistory(results.data))
    yield put(success({ typeLoading: type }))
  }
  // if (results.status === 'success') yield put(success({ typeLoading: type, chatbotResponse: results.data }))
}

function* getGPTEverythingSearch({ type, data }) {
  const results = yield call(boiler, 'getGPTEverythingSearch', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiEverythingSearch: results.data }))
}

function* getGPTDestinationDetails({ type, data }) {
  const results = yield call(boiler, 'getGPTDestinationDetails', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiDestinationDetails: results.data }))
}

function* getGPTDestinationExtras({ type, data }) {
  const results = yield call(boiler, 'getGPTDestinationExtras', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiDestinationExtras: results.data }))
}

function* getGPTDestinationRecommendations({ type, data }) {
  const results = yield call(boiler, 'getGPTDestinationRecommendations', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiDestinations: results.data }))
}

function* getGPTSimilarDestinations({ type, data }) {
  const results = yield call(boiler, 'getGPTSimilarDestinations', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiSimilarDestinations: results.data }))
}

function* getGPTRecommendedExperiences({ type, data }) {
  const results = yield call(boiler, 'getGPTRecommendedExperiences', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, aiRecommendedExperiences: results.data }))
}

function* getExternalCards() {
  const results = yield call(boiler, 'getExternalCards', { type: T.GET_EXTERNAL_CARDS })
  if (results.status === 'success') yield put(success({ typeLoading: T.GET_EXTERNAL_CARDS, externalCards: results.data }))
}

function* saveExternalCard({ type, data }) {
  const results = yield call(boiler, 'saveExternalCard', { type }, data)
  if (results.status === 'success') {
    yield call(getExternalCards)
    yield put(success({ typeLoading: type }))
  }
}

function* removeExternalCard({ type, unique_id }) {
  const results = yield call(boiler, 'removeExternalCard', { type }, unique_id)
  if (results.status === 'success') {
    yield call(getExternalCards)
    yield put(success({ typeLoading: type }))
  }
}

function* getCorporateExternalCard() {
  const results = yield call(boiler, 'getCorporateExternalCard', { type: T.GET_CORPORATE_EXTERNAL_CARD })
  if (results.status === 'success') yield put(success({ typeLoading: T.GET_CORPORATE_EXTERNAL_CARD, corporateExternalCard: results.data }))
}

function* removeCorporateExternalCard({ type, unique_id }) {
  const results = yield call(boiler, 'removeCorporateExternalCard', { type }, unique_id)
  if (results.status === 'success') yield put(success({ typeLoading: type, corporateExternalCard: {} }))
}

function* purchaseCompanyPoints({ type, data }) {
  const results = yield call(boiler, 'purchaseCompanyPoints', { type }, data)
  if (results.status === 'success') {
    yield call(getCompany)
    yield call(getCorporateExternalCard)
    yield put(success({ typeLoading: type }))
  }
}

// function* getFlightOffers({ type, data }) {
//   const results = yield call(boiler, 'getFlightOffers', { type }, data)
//   if (results.status === 'success') yield put(success({ typeLoading: type, flightOffers: results.data }))
// }

function* getCheapestFlight({ type, data }) {
  const results = yield call(boiler, 'getCheapestFlight', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, cheapestFlight: results.data }))
}

function* getFlightPriceAnalysis({ type, data }) {
  const results = yield call(boiler, 'getFlightPriceAnalysis', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, flightPriceAnalysis: results.data }))
}

function* getFlightInspiration({ type, data }) {
  const results = yield call(boiler, 'getFlightInspiration', { type }, data)
  if (results.status === 'success') yield put(success({ typeLoading: type, flightInspiration: results.data }))
}

function* sendUserPoints({ type, data }) {
  const results = yield call(boiler, 'sendUserPoints', { type }, data)
  if (results.status === 'success') {
    yield fork(getSavingsHistory, { search: '' })
    yield fork(getSavingsGraph)
    yield fork(getSavingsTotals)
    yield put(success({ typeLoading: type }))
  }
}

function* getCorporatePointsHistory({ month, year, page, page_size }) {
  const results = yield call(
    boiler,
    'getCorporatePointsHistory',
    { type: T.GET_CORPORATE_POINTS_HISTORY },
    month,
    year,
    page,
    page_size,
  )
  if (results.status === 'success') {
    yield put(
      success({
        corporatePointsHistory: results.data,
        typeLoading: T.GET_CORPORATE_POINTS_HISTORY,
      })
    )
  }
}

// function* searchTravelLeisureByGeo({ type, data }) {
//   yield put(clearFromState(['travelLeisureNearestRegions']))
// 	const results = yield call(boiler, 'searchTravelLeisure', { type }, data)
// 	if (results.status === 'success') {
// 		yield put(success({ travelLeisureNearestRegions: results.data, typeLoading: type }))
// 	} else {
// 		yield put(success({ travelLeisureNearestRegions: {}, typeLoading: type }))
// 	}
// }

function* searchTravelLeisure({ type, data }) {
  // yield put(clearFromState(['travelLeisureSearchResults']))
	const results = yield call(boiler, 'searchTravelLeisure', { type }, data)
	if (results.status === 'success') {
		yield put(
			success({ travelLeisureSearchResults: results.data, typeLoading: type }),
		)
	} else {
		yield put(success({ travelLeisureSearchResults: {}, typeLoading: type }))
	}
}

function* searchTravelLeisureArea({ type, search }) {
  yield put(clearFromState(['travelLeisureAreaSearchResults']))
  const results = yield call(boiler, 'searchTravelLeisureArea', { type }, search)
  if (results.status === 'success') {
    yield put(
      success({ travelLeisureAreaSearchResults: results.data?.areas, typeLoading: type }),
    )
  } else {
    yield put(success({ travelLeisureAreaSearchResults: [], typeLoading: type }))
  }
}

function* getTravelLeisureResort({ type, id }) {
  const results = yield call(boiler, 'getTravelLeisureResort', { type }, id)
  if (results.status === 'success') {
    yield put(success({ travelLeisureResort: results.data, typeLoading: type }))
  }
}

function* getTravelLeisureAvailabilities({ type, data }) {
  yield put(clearFromState(['travelLeisureAvailabilities']))
	const results = yield call(boiler, 'searchTravelLeisure', { type }, data)
	if (results.status === 'success') {
		yield put(success({ travelLeisureAvailabilities: results.data, typeLoading: type }))
	}
}

function* createTravelLeisureCart({ type, data }) {
  yield put(clearFromState(['travelLeisureCart']))
	const results = yield call(boiler, 'createTravelLeisureCart', { type }, data)
	if (results.status === 'success') {
		yield put(success({ travelLeisureCart: results.data, typeLoading: type }))
	}
}

function* bookTravelLeisure({ type, data }) {
  const results = yield call(boiler, 'bookTravelLeisure', { type }, data)
  // yield put(clearFromState(['longStayOrderNumber', 'hotelReservationStatus']))
  yield put(clearFromState(['longStayOrderNumber']))
  if (results.status === 'success') {
    yield put(success({ typeLoading: type, longStayOrderNumber: results.data }))
    yield fork(getSavingsTotals)
  }
}

function* getRewardBadges({ type, data }) {
	const results = yield call(boiler, 'getRewardBadges', { type }, data)
	if (results.status === 'success') {
		yield put(success({ rewardBadges: results.data, typeLoading: type }))
	}
}

export function* accountSagas() {
  yield takeLeading(T.DEV_RESET_USER, DEV_resetUser)
  yield takeLeading(T.DEV_RESET_CORPORATE_CUSTOMER, DEV_resetCorporateCustomer)
  yield takeLeading(T.DEV_VERIFICATION_TEST, DEV_verificationTest)
  yield takeLeading(T.DEV_RESET_PLAID_LOGIN, DEV_resetPlaidLogin)
  yield takeLeading(T.DEV_RESET_HRIS_INFORMATION, DEV_resetHRISInformation)
  yield takeLeading(T.GET_USER, getUser)
  yield takeLeading(T.SET_USER, setUser)
  yield takeLeading(T.SET_LAST_LOGIN, setLastLogin)
  yield takeLeading(T.GET_MY_TRIPS, getMyTrips)
  yield takeLeading(T.GET_PAST_TRIPS, getPastTrips)
  yield takeLeading(T.GET_ALL_TRIPS, getAllTrips)
  yield takeLeading(T.GET_TRIP, getTrip)
  yield takeLeading(T.GET_SAVINGS_TOTALS, getSavingsTotals)
  yield takeLeading(T.GET_SAVINGS_GOAL, getSavingsGoal)
  yield takeLeading(T.SET_SAVINGS_GOAL, setSavingsGoal)
  yield takeLeading(T.GET_SAVINGS_HISTORY, getSavingsHistory)
  yield takeLeading(T.GET_SAVINGS_GRAPH, getSavingsGraph)
  yield takeLeading(T.GET_ACCOUNT_DETAILS, getAccountDetails)
  yield takeLeading(T.SET_ACCOUNT_DETAILS, setAccountDetails)
  yield takeLeading(T.CREATE_PLAID_LINK_TOKEN, createPlaidLinkToken)
  yield takeLeading(T.RELINK_PLAID, relinkPlaid)
  yield takeLeading(T.GET_PLAID_ACCESS_TOKEN, getPlaidAccessToken)
  yield takeLeading(T.CLEAR_PLAID_STATUS, clearPlaidStatus)
  yield takeLeading(T.BOOST_ACCOUNT, boostAccount)
  yield takeLeading(T.MAKE_TRANSACTION, makeTransaction)
  yield takeLeading(T.MAKE_CORPORATE_TRANSACTION, makeCorporateTransaction)
  yield takeLeading(T.GET_TRAVEL_COMPANIONS, getTravelCompanions)
  yield takeLeading(T.GET_TRAVEL_PASSPORTS, getTravelPassports)
  yield takeLeading(T.CREATE_TRAVEL_PASSPORT, createTravelPassport)
  yield takeLeading(T.SET_TRAVEL_PASSPORT, setTravelPassport)
  yield takeLeading(T.DELETE_TRAVEL_PASSPORT, deleteTravelPassport)
  yield takeLeading(T.GET_TRAVEL_LOYALTIES, getTravelLoyalties)
  yield takeLeading(T.SET_TRAVEL_LOYALTY, setTravelLoyalty)
  yield takeLeading(T.UPDATE_TRAVEL_LOYALTY, updateTravelLoyalty)
  yield takeLeading(T.DELETE_TRAVEL_LOYALTY, deleteTravelLoyalty)
  yield takeLeading(T.GET_TRAVEL_LOYALTY_BY_TYPE, getTravelLoyaltyByType)
  yield takeLeading(T.GET_SETUP_STEPS, getSetupSteps)
  yield takeLeading(T.GET_SAVINGS_ACCOUNTS, getSavingsAccounts)
  yield takeLeading(T.GET_SAVINGS_PAYMENT_METHODS, getSavingsPaymentMethods)
  yield takeLeading(T.GET_NOTIFICATION_PREFERENCES, getNotificationPreferences)
  yield takeLeading(T.GET_INTENTIONS, getIntentions)
  yield takeLeading(T.GET_ONBOARDING, getOnboarding)
  yield takeLeading(T.GET_ONBOARDING_DOCUMENTS, getOnboardingDocuments)
  yield takeLeading(T.GET_ONBOARDING_DOCUMENT_STATUS, getOnboardingDocumentStatus)
  yield takeLeading(T.GET_ONBOARDING_AGREEMENTS, getOnboardingAgreements)
  yield takeLeading(T.SET_ONBOARDING_AGREEMENTS, setOnboardingAgreements)
  yield takeLeading(T.SET_ONBOARDING_GENERAL_INFO, setOnboardingGeneralInfo)
  yield takeLeading(T.SET_ONBOARDING_INCOME_INFO, setOnboardingIncomeInfo)
  yield takeLeading(T.SET_ONBOARDING_PERSONAL_IDENTIFICATION, setOnboardingPersonalIdentification)
  yield takeLeading(T.GET_ONBOARDING_STATUS, getOnboardingStatus)
  yield takeLeading(T.SUBMIT_KBA_ANSWERS, submitKBAAnswers)
  yield takeLeading(T.INIT_KBA, initKBA)
  yield takeLeading(T.GET_CORPORATE_ONBOARDING, getCorporateOnboarding)
  yield takeLeading(T.SET_CORPORATE_ONBOARDING, setCorporateOnboarding)
  yield takeLeading(T.GET_CORPORATE_ONBOARDING_DOCUMENTS, getCorporateOnboardingDocuments)
  yield takeLeading(T.SET_CORPORATE_ONBOARDING_DOCUMENTS, setCorporateOnboardingDocuments)
  yield takeLeading(T.GET_CORPORATE_ONBOARDING_AGREEMENTS, getCorporateOnboardingAgreements)
  yield takeLeading(T.SET_CORPORATE_ONBOARDING_AGREEMENTS, setCorporateOnboardingAgreements)
  yield takeLeading(T.CREATE_BENEFICIAL_OWNER, createBeneficialOwner)
  yield takeLeading(T.GET_BENEFICIAL_OWNER_DETAILS, getBeneficialOwnerDetails)
  yield takeLeading(T.GET_BENEFICIAL_OWNERS, getBeneficialOwners)
  yield takeLeading(T.DELETE_BENEFICIAL_OWNER, deleteBeneficialOwner)
  yield takeLeading(T.CERTIFY_BENEFICIAL_OWNERS, certifyBeneficialOwners)
  yield takeLeading(T.GET_CONTRIBUTION_SETTINGS, getContributionSettings)
  yield takeLeading(T.CREATE_CONTRIBUTION_SETTINGS, createContributionSettings)
  yield takeLeading(T.UPDATE_CONTRIBUTION_SETTINGS, updateContributionSettings)
  yield takeLeading(T.GET_EXTERNAL_ACCOUNTS, getExternalAccounts)
  yield takeLeading(T.DELETE_EXTERNAL_ACCOUNT, deleteExternalAccount)
  yield takeLeading(T.GET_CORPORATE_EXTERNAL_ACCOUNTS, getCorporateExternalAccounts)
  yield takeLeading(T.DELETE_CORPORATE_EXTERNAL_ACCOUNT, deleteCorporateExternalAccount)
  yield takeLeading(T.GET_PTO_EXPLAINER, getPTOExplainer)
  yield takeLeading(T.GET_EMPLOYEES, getEmployees)
  yield takeLeading(T.GET_MANAGERS, getManagers)
  yield takeLeading(T.GET_TEAM, getTeam)
  yield takeLeading(T.GET_MANAGER_TEAM, getManagerTeam)
  yield takeLeading(T.CREATE_EMPLOYEE, createEmployee)
  yield takeLeading(T.UPDATE_EMPLOYEE, updateEmployee)
  yield takeLeading(T.UPLOAD_EMPLOYEES, uploadEmployees)
  yield takeLeading(T.DELETE_EMPLOYEE, deleteEmployee)
  yield takeLeading(T.SET_EMPLOYEE_ROLE, setEmployeeRole)
  yield takeLeading(T.GET_BUSINESS_CLASSIFICATIONS, getBusinessClassifications)
  yield takeLeading(T.GET_SURVEY, getSurvey)
  yield takeLeading(T.SET_SURVEY, setSurvey)
  yield takeLeading(T.SET_TRAVEL_INTENTION, setTravelIntention)
  yield takeLeading(T.GET_COMPANY, getCompany)
  yield takeLeading(T.SET_COMPANY, setCompany)
  yield takeLatest(T.GET_COUNTRIES, getCountries)
  yield takeLatest(T.GET_AIRPORTS, getAirports)
  yield takeLatest(T.GET_AIRLINES, getAirlines)
  yield takeLatest(T.GET_STAYS, getStays)
  yield takeLatest(T.GET_LOCATIONS, getLocations)
  yield takeLeading(T.GET_OCEAN_CRUISES, getOceanCruises)
  yield takeLatest(T.GET_OCEAN_DESTINATIONS, getOceanDestinations)
  yield takeLatest(T.GET_OCEAN_CRUISE_TOURS, getOceanCruiseTours)
  yield takeLatest(T.GET_RIVER_CRUISES, getRiverCruises)
  yield takeLatest(T.GET_RIVER_DESTINATIONS, getRiverDestinations)
  yield takeLatest(T.GET_RIVER_CITIES, getRiverCities)
  yield takeLatest(T.GET_RIVER_COUNTRIES, getRiverCountries)
  yield takeLatest(T.GET_RIVER_LINES, getRiverLines)
  yield takeLatest(T.GET_RIVER_SHIPS, getRiverShips)
  yield takeLatest(T.GET_RIVER_WATERWAYS, getRiverWaterways)
  yield takeLatest(T.GET_TOUR_VENDORS, getTourVendors)
  yield takeLatest(T.GET_CAR_VENDORS, getCarVendors)
  yield takeLatest(T.GET_EXPERIENCE_LOCATIONS, getExperienceLocations)
  yield takeEvery(T.GET_EXPERIENCES, getExperiences)
  yield takeLeading(T.SEARCH_VIATOR, searchViator)
  yield takeEvery(T.GET_VIATOR_EXPERIENCES, getViatorExperiences)
  yield takeEvery(T.GET_VIATOR_AVAILABILITY, getViatorAvailability)
  yield takeEvery(T.GET_EXPERIENCE_TAGS, getExperienceTags)
  yield takeLeading(T.UPLOAD_ID_DOCUMENT, uploadIDDocument)
  yield takeLeading(T.GET_CORPORATE_ONBOARDING_STATUS, getCorporateOnboardingStatus)
  yield takeLeading(T.GET_CORPORATE_DOCUMENT_STATUS, getCorporateDocumentStatus)
  yield takeLeading(T.GET_AUTHORIZATION_TEXT, getAuthorizationText)
  yield takeLeading(T.COMPLETE_ACCOUNT, completeAccount)
  yield takeLeading(T.CLEAR_ACCOUNT_ERROR, clearAccountError)
  yield takeLeading(T.GET_CORPORATE_TRANSACTION_HISTORY, getCorporateTransactionHistory)
  yield takeLeading(T.GET_CORPORATE_WALLET_HISTORY, getCorporateWalletHistory)
  yield takeLeading(T.CANCEL_TRANSACTION, cancelTransaction)
  yield takeLeading(T.CANCEL_CORPORATE_TRANSACTION, cancelCorporateTransaction)
  yield takeLeading(T.CANCEL_MANAGER_TRANSACTION, cancelManagerTransaction)
  yield takeLeading(T.SET_BENEFICIAL_OWNER_DOCUMENTS, setBeneficialOwnerDocuments)
  yield takeLeading(T.GET_BENEFICIAL_OWNER_STATUS, getBeneficialOwnerStatus)
  yield takeLeading(T.ENCRYPT_TOKEN, encryptToken)
  yield takeLeading(T.GET_INTERNAL_ONBOARDING_AGREEMENTS, getInternalOnboardingAgreements)
  yield takeLeading(T.GET_INTERNAL_CORP_ONBOARDING_AGREEMENTS, getInternalCorpOnboardingAgreements)
  yield takeLeading(T.GET_COMPANY_MATCH, getCompanyMatch)
  yield takeLeading(T.CREATE_BOOKING_REQUEST, createBookingRequest)
  yield takeLeading(T.GET_EXPERIENCE_DETAILS, getExperienceDetails)
  yield takeLeading(T.GET_CONTENT_DETAILS, getContentDetails)
  yield takeEvery(T.GET_CONTENT, getContent)
  yield takeLeading(T.GET_CATEGORIES, getCategories)
  yield takeEvery(T.GET_VENDORS, getVendors)
  yield takeLeading(T.GET_FEATURED_VENDORS, getFeaturedVendors)
  yield takeLeading(T.GET_VENDOR, getVendor)
  yield takeLeading(T.GET_FAQ_CATEGORIES, getFAQCategories)
  yield takeLeading(T.GET_FAQ, getFAQ)
  yield takeEvery(T.BULK_EMPLOYEE_DELETE, bulkEmployeeDelete)
  yield takeEvery(T.BULK_EMPLOYEE_EDIT, bulkEmployeeEdit)
  yield takeEvery(T.BULK_EMPLOYEE_INVITE, bulkEmployeeInvite)
  yield takeLeading(T.INVITE_INACTIVE_USERS, inviteInactiveUsers)
  yield takeLatest(T.SEARCH_EMPLOYEES, searchEmployees)
  yield takeLatest(T.SEARCH_MANAGERS, searchManagers)
  yield takeLatest(T.GET_EMPLOYEE_UPLOAD_RESULTS, getEmployeeUploadResults)
  yield takeEvery(T.INVITE_USER, inviteUser)
  yield takeEvery(T.MAKE_BULK_CORPORATE_TRANSACTION, makeBulkCorporateTransaction)
  yield takeEvery(T.GET_BULK_EMPLOYEE_REWARD_ATTEMPTS, getBulkEmployeeRewardAttempts)
  yield takeEvery(T.GET_EMPLOYEE_UPLOAD_LOG, getEmployeeUploadLog)
  yield takeEvery(T.EMAIL_CORPORATE_TRANSACTION_HISTORY, emailCorporateTransactionHistory)
  yield takeEvery(T.CREATE_ITINERARY, createItinerary)
  yield takeEvery(T.UPDATE_ITINERARY, updateItinerary)
  yield takeEvery(T.REMOVE_ITINERARY, removeItinerary)
  yield takeEvery(T.MOVE_ITINERARY, moveItinerary)
  yield takeEvery(T.UPDATE_TRIP, updateTrip)
  yield takeEvery(T.DELETE_TRIP, deleteTrip)
  yield takeEvery(T.CREATE_TRIP, createTrip)
  yield takeEvery(T.MERGE_TRIPS, mergeTrips)
  yield takeEvery(T.GET_DESTINATION_CATEGORIES, getDestinationCategories)
  yield takeEvery(T.GET_TRAVEL_CATEGORIES, getTravelCategories)
  yield takeEvery(T.REQUEST_ITINERARY_CANCELLATION, requestItineraryCancellation)
  yield takeLeading(T.GET_ALL_BUDGETS, getAllBudgets)
  yield takeEvery(T.GET_MY_BUDGETS, getMyBudgets)
  yield takeEvery(T.GET_MANAGER_DETAILS, getManagerDetails)
  yield takeEvery(T.UPDATE_BUDGET, updateBudget)
  yield takeEvery(T.GET_BUDGET, getBudget)
  yield takeEvery(T.CREATE_BUDGET, createBudget)
  yield takeEvery(T.CREATE_BULK_BUDGET, createBulkBudget)
  yield takeEvery(T.DELETE_BUDGET, deleteBudget)
  yield takeEvery(T.REWARD_MANAGER_TO_EMPLOYEE, rewardManagerToEmployee)
  yield takeEvery(T.BULK_REWARD_MANAGER_TO_EMPLOYEE, bulkRewardManagerToEmployee)
  yield takeEvery(T.GET_BUDGET_HISTORY, getBudgetHistory)
  yield takeEvery(T.REQUEST_ADDITIONAL_BUDGET, requestAdditionalBudget)
  yield takeEvery(T.REQUEST_BUDGET, requestBudget)
  yield takeEvery(T.CREATE_MERGE_LINK_TOKEN, createMergeLinkToken)
  yield takeEvery(T.GET_MERGE_ACCESS_TOKEN, getMergeAccessToken)
  yield takeEvery(T.GET_REWARD_LOG, getRewardLog)
  yield takeEvery(T.GET_COMPANY_IMAGES, getCompanyImages)
  yield takeEvery(T.ADD_COMPANY_IMAGE, addCompanyImage)
  yield takeEvery(T.BULK_ADD_COMPANY_IMAGES, bulkAddCompanyImages)
  yield takeEvery(T.FLAG_COMPANY_IMAGE, flagCompanyImage)
  yield takeEvery(T.REMOVE_COMPANY_IMAGE, removeCompanyImage)
  yield takeEvery(T.BULK_REMOVE_COMPANY_IMAGES, bulkRemoveCompanyImages)
  yield takeLeading(T.GET_CUSTOMER_LOCATIONS, getCustomerLocations)
  yield takeLatest(T.GET_VOTED_LOCATIONS, getVotedLocations)
  yield takeEvery(T.VOTE_LOCATIONS, voteLocations)
  yield takeLatest(T.GET_COMPANY_SETTINGS, getCompanySettings)
  yield takeEvery(T.UPDATE_COMPANY_SETTINGS, updateCompanySettings)
  yield takeLatest(T.GET_COMPANY_PTO_EXPLAINER, getCompanyPTOExplainer)
  yield takeEvery(T.UPDATE_COMPANY_PTO_EXPLAINER, updateCompanyPTOExplainer)
  yield takeLeading(T.GET_USER_SETTINGS, getUserSettings)
  yield takeEvery(T.UPDATE_USER_SETTINGS, updateUserSettings)
  yield takeLatest(T.GET_TRAVEL_CURIOUS_PROMOTED_CITIES, getTravelCuriousPromotedCities)
  yield takeLatest(T.GET_TRAVEL_CURIOUS_CITIES, getTravelCuriousCities)
  yield takeLatest(T.GET_TRAVEL_CURIOUS_TOURS, getTravelCuriousTours)
  yield takeLatest(T.BOOK_TRAVEL_CURIOUS, bookTravelCurious)
  yield takeLatest(T.REQUEST_VIATOR_VC, requestViatorVC)
  yield takeLatest(T.SET_EMAIL_COMMUNICATION_PREFERENCES, setEmailCommunicationPreferences)
  yield takeEvery(T.BULK_ADD_TRIP_IMAGES, bulkAddTripImages)
  yield takeEvery(T.BULK_REMOVE_TRIP_IMAGES, bulkRemoveTripImages)
  yield takeLeading(T.GET_COMPANY_ACTIVITY, getCompanyActivity)
  yield takeLeading(T.GET_FIRST_SYNC_STATUS, getFirstSyncStatus)
  yield takeLeading(T.GET_HRIS_ACCOUNTS, getHRISAccounts)
  yield takeLatest(T.UPDATE_HRIS_ACCOUNT, updateHRISAccount)
  yield takeLatest(T.REMOVE_HRIS_ACCOUNT, removeHRISAccount)
  yield takeLeading(T.GET_HRIS_ELIGIBILITY, getHRISEligibility)
  yield takeEvery(T.UPDATE_HRIS_ELIGIBILITY, updateHRISEligibility)
  yield takeEvery(T.CONFIRM_ELIGIBILITY_RULES, confirmEligibilityRules)
  yield takeLeading(T.GET_HRIS_STATUS_COUNTS, getHRISStatusCounts)
  yield takeLatest(T.PROCESS_ELIGIBLE_EMPLOYEES, processEligibleEmployees)
  yield takeLatest(T.OVERRIDE_EMPLOYEE_ELIGIBILITY, overrideEmployeeEligibility)
  yield takeLatest(T.OVERRIDE_EMPLOYEE_INELIGIBILITY, overrideEmployeeIneligibility)
  yield takeLatest(T.CONFIRM_INELIGIBILITY, confirmIneligibility)
  yield takeLatest(T.GET_UNMERGED_EMPLOYEES, getUnmergedEmployees)
  yield takeLatest(T.SEARCH_HRIS_EMPLOYEES, searchHRISEmployees)
  yield takeLatest(T.GET_PAGINATED_HRIS_RECORDS, getPaginatedHRISRecords)
  yield takeLatest(T.MERGE_HRIS_EMPLOYEE, mergeHRISEmployee)
  yield takeLatest(T.DEACTIVATE_HRIS_EMPLOYEE, deactivateHRISEmployee)
  yield takeLeading(T.GET_HRIS_STATUS, getHRISStatus)
  yield takeLatest(T.SEND_HRIS_EMPLOYEE_INVITES, sendHRISEmployeeInvites)
  yield takeLeading(T.GET_COLLECTIONS, getCollections)
  yield takeLeading(T.GET_ALL_COLLECTIONS, getAllCollections)
  yield takeLeading(T.GET_COLLECTION, getCollection)
  yield takeLatest(T.CHANGE_COLLECTION_NAME, changeCollectionName)
  yield takeLatest(T.CREATE_COLLECTION, createCollection)
  yield takeLatest(T.DELETE_COLLECTION, deleteCollection)
  yield takeLatest(T.MERGE_COLLECTIONS, mergeCollections)
  yield takeLatest(T.CONNECT_COLLECTION_TO_TRIP, connectCollectionToTrip)
  yield takeLatest(T.DISCONNECT_COLLECTION_FROM_TRIP, disconnectCollectionFromTrip)
  yield takeLeading(T.GET_FAVORITES, getFavorites)
  yield takeLatest(T.ADD_FAVORITE, addFavorite)
  yield takeLatest(T.REMOVE_FAVORITE, removeFavorite)
  yield takeLatest(T.ADD_CONTENT_TO_COLLECTION, addContentToCollection)
  yield takeLatest(T.REMOVE_CONTENT_FROM_COLLECTION, removeContentFromCollection)
  yield takeLeading(T.GET_RECENT_CONTENT, getRecentContent)
  yield takeLeading(T.GET_BOOKING_STATS, getBookingStats)
  yield takeLeading(T.GET_BUDGET_STATS, getBudgetStats)
  yield takeLeading(T.GET_EMPLOYEE_STATS, getEmployeeStats)
  yield takeLeading(T.GET_FUNDING_STATS, getFundingStats)
  yield takeLeading(T.GET_EVOLVE_MIGRATION_SETTING, getEvolveMigrationSetting)
  yield takeLatest(T.GET_HOTELS, getHotels)
  yield takeLatest(T.SEARCH_NEARBY_STAYS, searchNearbyStays)
  yield takeLeading(T.GET_HOTEL_AMENITIES, getHotelAmenities)
  yield takeLeading(T.GET_HOTEL, getHotel)
  yield takeLeading(T.GET_HOTEL_AVAILABILITY, getHotelAvailability)
  yield takeLeading(T.BOOK_HOTEL_PLANNER, bookHotelPlanner)
  yield takeLatest(T.GET_HOTEL_PLANNER_RESERVATION_STATUS, getHotelPlannerReservationStatus)
  yield takeEvery(T.REQUEST_HOTEL_PLANNER_CANCELLATION, requestHotelPlannerCancellation)
  yield takeLeading(T.GET_RANDOM_DESTINATIONS, getRandomDestinations)
  yield takeLeading(T.GET_SEARCH_HISTORY, getSearchHistory)
  yield takeLatest(T.ADD_TO_SEARCH_HISTORY, addToSearchHistory)
  yield takeLeading(T.GENERAL_ERROR, generalError)
  yield takeLeading(T.GET_MATCH_HISTORY, getMatchHistory)
  yield takeLeading(T.GET_PAYROLL_ACCESS_TOKEN, getPayrollAccessToken)
  yield takeLeading(T.REQUEST_ADMIN_PAYROLL_DISCONNECT, requestAdminPayrollDisconnect)
  yield takeLeading(T.SET_PAYROLL_DISCONNECT_REQUEST, setPayrollDisconnectRequest)
  yield takeLeading(T.REMOVE_PAYROLL, removePayroll)
  yield takeLeading(T.REQUEST_PARTNER_VC, requestPartnerVC)
  yield takeLeading(T.REQUEST_FLIGHT_VC, requestFlightVC)
  yield takeLeading(T.GET_MY_REWARDS, getMyRewards)
  yield takeLeading(T.GET_INSURANCE_PRODUCTS, getInsuranceProducts)
  yield takeLeading(T.GET_INSURANCE_PRODUCT_STATES, getInsuranceProductStates)
  yield takeLeading(T.GET_INSURANCE_PRODUCTS_HOST_COUNTRIES, getInsuranceProductsHostCountries)
  yield takeLeading(T.GET_INSURANCE_PRODUCTS_DESTINATION_COUNTRIES, getInsuranceProductsDestinationCountries)
  yield takeLeading(T.GET_INSURANCE_PRODUCTS_BY_STATE, getInsuranceProductsByState)
  yield takeLeading(T.GET_INSURANCE_UPFRONT_QUOTE, getInsuranceUpfrontQuote)
  yield takeLeading(T.GET_INSURANCE_FINAL_QUOTE, getInsuranceFinalQuote)
  yield takeLeading(T.BOOK_INSURANCE, bookInsurance)
  yield takeLeading(T.GET_INSURANCE_PAYMENT, getInsurancePayment)
  yield takeLeading(T.GET_INSURANCE_POLICY_FILES, getInsurancePolicyFiles)
  yield takeLeading(T.GET_INSURANCE_POLICY_TRIP, getInsurancePolicyTrip)
  yield takeLeading(T.GET_INSURANCE_POLICY_DETAILS, getInsurancePolicyDetails)
  yield takeLeading(T.CONNECT_POLICY_TO_TRIP, connectPolicyToTrip)
  yield takeLeading(T.REMOVE_POLICY_FROM_TRIP, removePolicyFromTrip)
  yield takeLeading(T.GET_POLICIES, getPolicies)
  yield takeLeading(T.CREATE_KYB_BUSINESS, createKYBBusiness)
  yield takeLeading(T.GET_KYB_INQUIRY_ID, getKYBInquiryID)
  yield takeLeading(T.SET_KYB_UNDER_REVIEW, setKYBUnderReview)
  yield takeLeading(T.GET_MAX_WITHDRAWAL, getMaxWithdrawal)
  yield takeLeading(T.REQUEST_FUND_WITHDRAWAL, requestFundWithdrawal)
  yield takeLeading(T.GET_USER_STATEMENT_PDF, getUserStatementPDF)
  yield takeLeading(T.GET_CORPORATE_STATEMENT_PDF, getCorporateStatementPDF)
  yield takeLeading(T.GET_CORPORATE_STATEMENT, getCorporateStatement)
  yield takeLeading(T.GET_BANK_AUTHORIZATION, getBankAuthorization)
  yield takeLeading(T.SET_MIGRATION_STATUS, setMigrationStatus)
  yield takeLeading(T.CHECK_PENDING_TRANSACTIONS, checkPendingTransactions)
  yield takeLeading(T.GET_AFFILIATES, getAffiliates)
  yield takeLeading(T.GET_OFFERS, getOffers)
  yield takeLeading(T.GET_FLIGHT_OFFERS, getFlightOffers)
  yield takeLeading(T.REQUEST_AFFILIATE_VCC, requestAffiliateVCC)
  yield takeLeading(T.REQUEST_GENERAL_VC, requestGeneralVC)
  yield takeLeading(T.SEND_AMOUNT_DISPUTE_TICKET, sendAmountDisputeTicket)
  yield takeLeading(T.GET_GENERAL_SETTING, getGeneralSetting)
  yield takeEvery(T.GET_DESTINATION_IMAGE, getDestinationImage)
  yield takeLeading(T.GET_VIRTUAL_CARDS, getVirtualCards)
  yield takeLeading(T.GET_VIRTUAL_CARD, getVirtualCard)
  yield takeLeading(T.GET_VIRTUAL_CARD_LINK, getVirtualCardLink)
  yield takeLeading(T.CANCEL_VIRTUAL_CARD, cancelVirtualCard)
  yield takeLeading(T.REPORT_VIRTUAL_CARD_ISSUE, reportVirtualCardIssue)
  yield takeLeading(T.GET_AI_MODELS, getAIModels)
  yield takeLeading(T.GET_AI_MODEL, getAIModel)
  yield takeEvery(T.SEND_AI_CHAT, sendAIChat)
  yield takeEvery(T.SEND_AI_COMPLETION, sendAICompletion)
  yield takeLeading(T.GET_AI_FINE_TUNE, getAIFineTune)
  yield takeLeading(T.SEND_CUSTOMER_SERVICE_REQUEST, sendCustomerServiceRequest)
  yield takeLeading(T.REQUEST_REIMBURSEMENT, requestReimbursement)
  yield takeLeading(T.GET_MONTHLY_DEAL, getMonthlyDeal)
  yield takeLatest(T.GET_EXPERIENCES_AUTOCOMPLETE, getExperiencesAutocomplete)
  yield takeLatest(T.SEARCH_HOLIBOB, searchHolibob)
  yield takeLatest(T.SEARCH_NEARBY_EXPERIENCES, searchNearbyExperiences)
  yield takeLeading(T.GET_HOLIBOB_EXPERIENCE_TAGS, getHolibobExperienceTags)
  yield takeLeading(T.GET_HOLIBOB_CATEGORIES, getHolibobCategories)
  yield takeLeading(T.GET_HOLIBOB_PLACES, getHolibobPlaces)
  yield takeLeading(T.GET_HOLIBOB_PRODUCT_DETAILS, getHolibobProductDetails)
  yield takeLatest(T.GET_HOLIBOB_PRODUCT_AVAILABILITY_LIST, getHolibobProductAvailabilityList)
  yield takeLeading(T.GET_HOLIBOB_PRODUCT_AVAILABILITY, getHolibobProductAvailability)
  yield takeEvery(T.GET_HOLIBOB_PRODUCT_OPTIONS, getHolibobProductOptions)
  yield takeEvery(T.GET_HOLIBOB_PRODUCT_PRICING, getHolibobProductPricing)
  yield takeLeading(T.BOOK_HOLIBOB, bookHolibob)
  yield takeLeading(T.ADD_HOLIBOB_AVAILABILITY_TO_BOOKING, addHolibobAvailabilityToBooking)
  yield takeLeading(T.GET_HOLIBOB_BOOKING, getHolibobBooking)
  yield takeLeading(T.CREATE_HOLIBOB_BOOKING, createHolibobBooking)
  yield takeLeading(T.SUBMIT_HOLIBOB_BOOKING_ANSWERS, submitHolibobBookingAnswers)
  yield takeLeading(T.GET_HOLIBOB_BOOKING_VOUCHER, getHolibobBookingVoucher)
  yield takeLeading(T.REQUEST_HOLIBOB_CANCELLATION, requestHolibobCancellation)
  yield takeLeading(T.GET_CHATBOT_RESPONSE, getChatbotResponse)
  yield takeLeading(T.GET_GPT_EVERYTHING_SEARCH, getGPTEverythingSearch)
  yield takeLeading(T.GET_GPT_DESTINATION_DETAILS, getGPTDestinationDetails)
  yield takeLeading(T.GET_GPT_DESTINATION_EXTRAS, getGPTDestinationExtras)
  yield takeLeading(T.GET_GPT_DESTINATION_RECOMMENDATIONS, getGPTDestinationRecommendations)
  yield takeLeading(T.GET_GPT_SIMILAR_DESTINATIONS, getGPTSimilarDestinations)
  yield takeLeading(T.GET_GPT_RECOMMENDED_EXPERIENCES, getGPTRecommendedExperiences)
  yield takeLeading(T.GET_EXTERNAL_CARDS, getExternalCards)
  yield takeLeading(T.SAVE_EXTERNAL_CARD, saveExternalCard)
  yield takeLeading(T.REMOVE_EXTERNAL_CARD, removeExternalCard)
  yield takeLeading(T.GET_CORPORATE_EXTERNAL_CARD, getCorporateExternalCard)
  yield takeLeading(T.REMOVE_CORPORATE_EXTERNAL_CARD, removeCorporateExternalCard)
  yield takeLeading(T.PURCHASE_COMPANY_POINTS, purchaseCompanyPoints)
  // yield takeLeading(T.GET_FLIGHT_OFFERS, getFlightOffers)
  yield takeLeading(T.GET_CHEAPEST_FLIGHT, getCheapestFlight)
  yield takeLeading(T.GET_FLIGHT_PRICE_ANALYSIS, getFlightPriceAnalysis)
  yield takeLeading(T.GET_FLIGHT_INSPIRATION, getFlightInspiration)
  yield takeLeading(T.SEND_USER_POINTS, sendUserPoints)
  yield takeLeading(T.GET_CORPORATE_POINTS_HISTORY, getCorporatePointsHistory)
  // yield takeLatest(T.SEARCH_TRAVEL_LEISURE_BY_GEO, searchTravelLeisureByGeo)
  yield takeLatest(T.SEARCH_TRAVEL_LEISURE, searchTravelLeisure)
  yield takeLatest(T.SEARCH_TRAVEL_LEISURE_AREA, searchTravelLeisureArea)
  yield takeLeading(T.GET_TRAVEL_LEISURE_RESORT, getTravelLeisureResort)
  yield takeLeading(T.GET_TRAVEL_LEISURE_AVAILABILITIES, getTravelLeisureAvailabilities)
  yield takeLeading(T.CREATE_TRAVEL_LEISURE_CART, createTravelLeisureCart)
  yield takeLeading(T.BOOK_TRAVEL_LEISURE, bookTravelLeisure)
  yield takeLeading(T.GET_REWARD_BADGES, getRewardBadges)
}
